import {
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  OnInit,
  Signal,
  signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import {
  FormBuilder,
  FormRecord,
  FormsModule,
  ReactiveFormsModule,
  ValidatorFn,
  Validators
} from '@angular/forms';
import { GoogleMap, MapAdvancedMarker } from '@angular/google-maps';
import {
  catchError,
  combineLatest,
  debounceTime,
  EMPTY,
  filter,
  finalize,
  firstValueFrom,
  forkJoin,
  map,
  Observable,
  of,
  pairwise,
  skip,
  startWith,
  switchMap,
  take
} from 'rxjs';
import { Dialog, DialogModule, DialogRef } from '@angular/cdk/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { select, Store } from '@ngxs/store';
import { ToastrService } from 'ngx-toastr';

import { OptionSwitcherComponent } from '../../../../shared/components/controllers/option-switcher/option-switcher.component';
import { OptionSwitcherItem } from '../../../../shared/interfaces/option-switcher.interface';
import { TipSelectorComponent } from '../../../../shared/components/tip-selector/tip-selector.component';
import { BackButtonComponent } from '../../../../shared/components/back-button/back-button.component';
import { ShippingOption } from '../../models/delivery-methods';
import { slideInOut } from '../../../../shared/transitions/slide-in-out.transition';
import { FormattedCurrencyPipe } from '../../../../shared/pipes/formatted-currency.pipe';
import {
  WooCommercePaymentField,
  WooCommercePaymentMethod
} from '../../../../core/interfaces/woo-commerce/api/payments/payments-api.interface';
import { ButtonSize } from '../../../../shared/enums/button.enum';
import {
  WooCommerceShippingMethods,
  WooCommerceShippingService
} from '../../../../core/services/woo-commerce/woo-commerce-shipping.service';
import { AddressPipe } from '../../../../shared/pipes/address.pipe';
import { PhonePipe } from '../../../../shared/pipes/phone.pipe';
import { BaseWooCommerceAddressDetails } from '../../../../core/interfaces/woo-commerce/woo-commerce.interface';
import {
  CheckoutOrderParams,
  WooCommerceCheckoutApiService
} from '../../../../core/services/woo-commerce/api/woo-commerce-checkout-api.service';
import { WooCommerceCartApiService } from '../../../../core/services/woo-commerce/api/woo-commerce-cart-api.service';
import {
  HappyCartAddressRequest,
  HappyPaymentGateway,
  HappyCartValidateResponse
} from '../../../../core/interfaces/happy-api/happy-api.interface';
import {
  CheckoutDropoffDialogComponent,
  CheckoutDropoffDialogResponse
} from '../dialogs/checkout-dropoff-dialog/checkout-dropoff-dialog.component';
import { ClickableRowComponent } from '../../../../shared/components/clickable-row/clickable-row.component';
import { EditableRowComponent } from '../../../../shared/components/editable-row/editable-row.component';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { PaymentFieldsComponent } from '../../../../shared/components/payment-fields/payment-fields.component';
import { CheckoutDetailsCartComponent } from './checkout-details-cart/checkout-details-cart.component';
import { CheckoutDetailsCouponComponent } from './checkout-details-coupon/checkout-details-coupon.component';
import { DynamicValidationDependencies } from '../../../../shared/components/controllers/control-error/validation-error-messages.token';
import { CardSelectorComponent } from '../../../../shared/components/controllers/card-selector/card-selector.component';
import { CardSelectorOption } from '../../../../shared/components/controllers/card-selector/card-selector.interface';
import {
  mapShippingOptionsToCardOptions,
  findFirstEligibleMethod
} from '../../../../shared/utils/card-selector.util';
import { CheckoutNotesDialogComponent } from '../dialogs/checkout-notes-dialog/checkout-notes-dialog.component';
import {
  CheckoutDeliveryAddressDialogComponent,
  CheckoutSelectAddressDialogResponse
} from '../dialogs/checkout-delivery-address-dialog/checkout-delivery-address-dialog.component';
import { WooCommerceCheckoutResponse } from '../../../../core/interfaces/woo-commerce/api/checkout/checkout-api.interface';
import { openGoogleMapsLocation } from '../../../../shared/utils/google-maps.util';
import {
  GoogleMapDecodedAddress,
  GoogleMapService
} from '../../../../core/services/google/google-map.service';
import {
  GetPickupLocationsAction,
  GetRestaurantGeneralAction,
  GetRestaurantPaymentMethodsAction,
  GetRestaurantProductCategoriesAction,
  GetRestaurantProductsAction
} from '../../../../shared/state/restaurant/restaurant.actions';
import {
  CartCouponAction,
  CartItemsAction,
  CartItemsActionGetPayload
} from '../../../../shared/state/cart/cart.actions';
import { CartState } from '../../../../shared/state/cart/cart.state';
import {
  WooCommerceCartFee,
  WooCommerceCartInfo,
  WooCommerceCartItem,
  WooCommerceCartTotals
} from '../../../../core/interfaces/woo-commerce/api/cart/cart.interface';
import { WooCommerceCartTokenService } from '../../../../core/services/woo-commerce/woo-commerce-cart-token.service';
import {
  RestaurantGeneralDeliveryTypes,
  RestaurantGeneralLeadTime,
  RestaurantState
} from '../../../../shared/state/restaurant/restaurant.state';
import { ShippingMethod } from '../../../../core/enums/woo-commerce/shipping/shipping.enum';
import { getShippingOptions } from '../../../../shared/constants/shipping-options.constant';
import { DeliveryState } from '../../../../shared/state/delivery/delivery.state';
import { SetDeliveryAddresses } from '../../../../shared/state/delivery/delivery.actions';
import {
  WooCommerceDatetimeTimeSlot,
  WooCommerceDeliveryAddress,
  WooCommerceShippingAddress
} from '../../../../core/interfaces/woo-commerce/api/woo-commerce-api.interface';
import {
  ShippingScheduleStateModel,
  ShippingState,
  ShippingStateModel
} from '../../../../shared/state/shipping/shipping.state';
import { AuthState } from '../../../../shared/state/auth/auth.state';
import {
  SetAvailableShippingMethodsAction,
  UpsertShippingAction
} from '../../../../shared/state/shipping/shipping.actions';
import {
  DateTimeDialogComponent,
  DateTimeDialogData
} from '../../../../shared/components/dialogs/date-time-dialog/date-time-dialog.component';
import { ConfirmDialogComponent } from '../../../../shared/components/dialogs/confirm-dialog/confirm-dialog.component';
import {
  convertToDate,
  convertToString,
  getWeekdayName
} from '../../../../shared/utils/date.utils';
import { WooCommerceRestaurantHoursValue } from '../../../../core/interfaces/woo-commerce/api/restaurant/restaurant-api.interface';
import {
  getTimeDiapasonByTime,
  parseTimeRange
} from '../../../../shared/utils/time.util';
import { TooltipDirective } from '../../../../shared/directives/tooltip.directive';
import {
  getPickupTimeOptions,
  PickupOption
} from '../../consts/pickup-time-options.const';
import { Router } from '@angular/router';
import { WooCommerceDatetimeTimeSlotValue } from '../../../../core/enums/woo-commerce/api/woo-commerce-api.enum';
import { PhoneValidators } from '../../../../shared/validators/phone.validators';
import { WooCommerceCoupon } from '../../../../core/interfaces/woo-commerce/api/coupon/coupon-api.interface';
import { CreditCardValidators } from '../../../../shared/validators/credit-card.validators';
import { CheckboxComponent } from '../../../../shared/components/controllers/checkbox/checkbox.component';
import { normalizeUSPhoneNumber } from '../../../../shared/utils/phone-normalizer.util';
import {
  GA4Item,
  GoogleAnalyticsService
} from '../../../../core/services/google-analytics.service';
import { WooCommerceShippingDatetimeApiService } from '../../../../core/services/woo-commerce/api/woo-commerce-shipping-datetime-api.service';
import { WooCommercePickupLocationDatetimeResponse } from '../../../../core/interfaces/woo-commerce/api/shipping-datetime/shipping-datetime-api.interface';
import { convertHtml2Text } from '../../../../shared/utils/text.utils';
import { AddOrderAction } from '../../../../shared/state/orders/orders.actions';
import { WooCommerceTipsApiService } from '../../../../core/services/woo-commerce/api/woo-commerce-tips-api.service';
import {
  getUnavailableCartItemsForSchedule,
  resolveCartCategories
} from '../../../../shared/utils/category-availability.util';
import { filterDatesByWeekdayAvailability } from '../../../../shared/utils/shipping-weekday-availability.util';
import { CloverPaymentComponent } from '../../../../shared/components/clover-payment/clover-payment.component';
import {
  CloverChargeError,
  CloverPaymentService
} from '../../../../core/services/clover/clover-payment.service';
import { HappyStoreSwitchService } from '../../../../core/services/happy/happy-store-switch.service';
import { getPickupLocationBackendId } from '../../../../shared/utils/pickup-location.util';
import { HappyStoreStorageService } from '../../../../core/services/happy/happy-store-storage.service';
import { HappyStoreSelectionService } from '../../../../core/services/happy/happy-store-selection.service';
import { WooCommerceCustomerPaymentApiService } from '../../../../core/services/woo-commerce/api/woo-commerce-customer-payment-api.service';
import { PaymentMethodResponse } from '../../../../core/interfaces/woo-commerce/api/payment-method/payment-method.interface';
import { ApplePayButtonComponent } from '../../../../shared/components/apple-pay-button/apple-pay-button.component';
import {
  ApplePayChargeError,
  ApplePayService,
  ApplePayTokenResult
} from '../../../../core/services/apple-pay/apple-pay.service';
import { StripeCardElementComponent } from '../../../../shared/components/stripe-card-element/stripe-card-element.component';
import { environment } from '../../../../../environments/environment';
import { AuthDialogService } from '../../../auth/services/auth-dialog.service';

@UntilDestroy()
@Component({
  selector: 'app-checkout-details',
  imports: [
    CloverPaymentComponent,
    ApplePayButtonComponent,
    GoogleMap,
    OptionSwitcherComponent,
    TipSelectorComponent,
    FormsModule,
    ReactiveFormsModule,
    BackButtonComponent,
    DialogModule,
    FormattedCurrencyPipe,
    AddressPipe,
    PhonePipe,
    ClickableRowComponent,
    EditableRowComponent,
    ButtonComponent,
    CheckoutDetailsCartComponent,
    CheckoutDetailsCouponComponent,
    CardSelectorComponent,
    PaymentFieldsComponent,
    ...DynamicValidationDependencies,
    MapAdvancedMarker,
    CheckboxComponent,
    TooltipDirective,
    StripeCardElementComponent
  ],
  animations: [slideInOut],
  templateUrl: './checkout-details.component.html',
  styleUrl: './checkout-details.component.scss'
})
export class CheckoutDetailsComponent implements OnInit, OnDestroy {
  private readonly authDialog = inject(AuthDialogService);

  protected readonly ButtonSize = ButtonSize;
  protected readonly ShippingMethod = ShippingMethod;
  protected readonly openGoogleMapsLocation = openGoogleMapsLocation;
  protected readonly normalizeUSPhoneNumber = normalizeUSPhoneNumber;
  protected readonly stripePublishableKey = environment.stripe.publishableKey;
  protected readonly UBER_OUT_OF_RANGE_MESSAGE =
    'You are out of Range for Uber delivery, please use pick-up instead.';

  @ViewChild('cloverPayment') cloverPayment?: CloverPaymentComponent;
  @ViewChild('stripeCardElement') stripeCardElement?: StripeCardElementComponent;

  // Timer for schedule expiration check
  private scheduleExpirationCheckId: ReturnType<typeof setInterval> | null =
    null;
  private readonly SCHEDULE_CHECK_INTERVAL_MS = 60000; // Check every minute
  private visibilityChangeHandler: (() => void) | null = null;
  protected readonly SHIPPING_OPTIONS: Signal<OptionSwitcherItem[]> = computed(
    () =>
      getShippingOptions(
        this.leadTimes().delivery_lead_time,
        this.leadTimes().pick_up_lead_time,
        this.deliveryTypes()
      )
  );

  protected readonly DELIVERY_TIME_OPTIONS = computed(() =>
    getPickupTimeOptions(
      this.leadTimes().delivery_lead_time,
      this.normalizedShippingSchedule() || undefined
    )
  );

  protected readonly PICKUP_TIME_OPTIONS = computed(() =>
    getPickupTimeOptions(
      this.leadTimes().pick_up_lead_time,
      this.normalizedShippingSchedule() || undefined,
      { includeStandard: this.isRestaurantOpen() }
    )
  );

  // Google Maps
  protected readonly GOOGLE_MAP_OPTIONS: google.maps.MapOptions = {
    fullscreenControl: false
  };

  googleMapZoom: number = 17;
  googleMapCenter: google.maps.LatLngLiteral = { lat: 40.7128, lng: -74.006 };
  googleMapMarkers: google.maps.LatLngLiteral[] = [];

  checkoutForm!: FormRecord;
  expandedPaymentMethodId: WritableSignal<string | null> = signal(null);

  leadTimes: Signal<RestaurantGeneralLeadTime> = select(
    RestaurantState.leadTimes
  );
  deliveryTypes: Signal<RestaurantGeneralDeliveryTypes> = select(
    RestaurantState.deliveryTypes
  );

  // Cart
  cartItems: Signal<WooCommerceCartItem[]> = select(CartState.items);
  isAuthenticated: Signal<boolean> = select(AuthState.isAuthenticated);

  hasForcedSubscription: Signal<boolean> = select(CartState.hasForcedSubscription);
  hasSubscriptionOption: Signal<boolean> = select(CartState.hasSubscriptionOption);

  isCartLoading: Signal<boolean> = select(CartState.isLoading('cart'));
  isSyncLoading: Signal<boolean> = select(CartState.isLoading('sync'));
  isAddItemLoading: Signal<boolean> = select(CartState.isLoading('addItem'));
  isCheckoutLocked: Signal<boolean> = computed(
    () =>
      this.isCartLoading() || this.isSyncLoading() || this.isAddItemLoading()
  );

  totals: Signal<WooCommerceCartTotals> = select(CartState.totals);
  fees: Signal<WooCommerceCartFee[]> = select(CartState.fees);
  coupons: Signal<WooCommerceCoupon[]> = select(CartState.coupons);
  driverFee: Signal<WooCommerceCartFee | undefined> = select(
    CartState.driverFee
  );

  // Payment Methods
  paymentMethods: Signal<WooCommercePaymentMethod[]> = select(
    RestaurantState.paymentMethods
  );

  // Delivery
  deliveryAddresses: Signal<WooCommerceDeliveryAddress[]> = select(
    DeliveryState.deliveryAddresses
  );

  // Pickup
  pickupAddresses: Signal<WooCommerceShippingAddress[]> = select(
    RestaurantState.pickupLocations
  );
  currentStorePickupAddresses: Signal<WooCommerceShippingAddress[]> = computed(() => {
    const selectedStore = this.happyStoreSelectionService.getSelected();
    const pickupAddresses = this.pickupAddresses();

    if (!selectedStore) {
      return pickupAddresses;
    }

    return pickupAddresses.filter(
      (address) =>
        address.storeApiKey === selectedStore.storeApiKey &&
        address.tenant === selectedStore.tenant
    );
  });
  isRestaurantOpen: Signal<boolean> = select(RestaurantState.isOpen);
  isTodayHoliday: Signal<boolean> = select(RestaurantState.isTodayHoliday);

  // Loaders
  isSubmitLoading: WritableSignal<boolean> = signal(false);
  isShippingDateTimesLoading: WritableSignal<boolean> = signal(false);
  isLoadingShippingMethods: WritableSignal<boolean> = signal(false);
  isUpdatingShippingRate: WritableSignal<boolean> = signal(false);
  isApplyingTip: WritableSignal<boolean> = signal<boolean>(false);
  private shouldSyncShippingRateAfterCartSync: boolean = false;

  // Available Shipping Methods (for delivery)
  availableShippingMethods: Signal<ShippingOption[]> = select(
    ShippingState.availableShippingMethods
  );
  selectedShippingMethod: Signal<ShippingOption | undefined> = select(
    ShippingState.selectedShippingMethod
  );
  shippingSchedule: Signal<ShippingScheduleStateModel | undefined> = select(
    ShippingState.schedule
  );

  // Normalize schedule data - convert date strings back to Date objects
  // This is needed because NGXS storage plugin serializes Date objects to strings
  normalizedShippingSchedule = computed(() => {
    const schedule = this.shippingSchedule();
    if (!schedule) {
      return undefined;
    }

    // Convert date string to Date object if needed
    let normalizedDate: Date;
    if (schedule.date instanceof Date) {
      normalizedDate = schedule.date;
    } else if (typeof schedule.date === 'string') {
      normalizedDate = new Date(schedule.date);
      // Validate the date
      if (isNaN(normalizedDate.getTime())) {
        console.warn('Invalid date in schedule:', schedule.date);
        return undefined;
      }
    } else {
      normalizedDate = new Date(schedule.date);
    }

    // Keep time as-is - formatScheduleTime will handle the conversion
    // formatScheduleTime can handle both Date objects and ISO strings (including epoch dates)
    let normalizedTime: Date | WooCommerceDatetimeTimeSlotValue | string;
    if (schedule.time instanceof Date) {
      normalizedTime = schedule.time;
    } else if (typeof schedule.time === 'string') {
      // Check if it's an ISO date string (including epoch dates like 1970-01-01T08:10:00.000Z)
      const isoDateMatch = schedule.time.match(
        /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/
      );
      if (isoDateMatch) {
        // Convert to Date object - formatScheduleTime will extract time correctly
        // even for epoch dates (1970-01-01) since we only care about hours/minutes
        const dateObj = new Date(schedule.time);
        if (!isNaN(dateObj.getTime())) {
          normalizedTime = dateObj;
        } else {
          // Invalid date string, keep as string and let formatScheduleTime handle it
          normalizedTime = schedule.time;
        }
      } else {
        // It's a time slot string (e.g., "09:00 AM - 10:00 AM") or "asap"
        normalizedTime = schedule.time;
      }
    } else {
      normalizedTime = schedule.time;
    }

    return {
      date: normalizedDate,
      time: normalizedTime,
      timestamp: schedule.timestamp
    };
  });

  // Computed card options for card selector (reactively re-evaluates when cart changes)
  availableShippingCardOptions = computed(() => {
    const totals = this.totals();
    const minorUnit = totals?.currency_minor_unit || 2;
    const subtotal =
      parseFloat(totals?.total_items || '0') / Math.pow(10, minorUnit);
    const itemCount = this.cartItems().length;
    return mapShippingOptionsToCardOptions(
      this.availableShippingMethods(),
      subtotal,
      itemCount
    );
  });

  // Computed selected shipping method price
  selectedShippingMethodPrice = computed(() => {
    const method = this.selectedShippingMethod();
    return method?.price || 0;
  });

  // Computed Apple Pay amount formatted as a string (e.g. "12.50")
  applePayAmount = computed(() => {
    const totals = this.totals();
    const minorUnit = totals?.currency_minor_unit || 2;
    const totalPrice = parseFloat(totals?.total_price || '0');
    const divisor = Math.pow(10, minorUnit);
    return (totalPrice / divisor).toFixed(2);
  });

  // Signal for selected payment method ID - reactive to form changes
  private selectedPaymentMethodId = signal<string | null>(null);

  isCardPayment = computed(() => {
    const selectedPaymentMethodId = this.selectedPaymentMethodId();
    if (!selectedPaymentMethodId) return false;

    const pm = this.paymentMethods().find(
      (m) => m.id === selectedPaymentMethodId
    );
    if (!pm) return false;

    if (pm.supports_tokenization) return true;

    const gateway = (pm.gateway || '').toLowerCase();
    return gateway === 'stripe' || gateway === 'authorize_net';
  });

  selectedPaymentGateway = computed((): HappyPaymentGateway | null => {
    const id = this.selectedPaymentMethodId();
    if (!id) return null;
    const pm = this.paymentMethods().find((m) => m.id === id);
    return pm?.gateway ?? null;
  });

  savedCards: WritableSignal<PaymentMethodResponse[]> = signal([]);
  selectedSavedCard: WritableSignal<PaymentMethodResponse | null> = signal(null);
  savedCardsLoading: WritableSignal<boolean> = signal(false);
  usingSavedCard = computed(() => this.selectedSavedCard() !== null);

  billingAddresses: WritableSignal<WooCommerceDeliveryAddress[]> = signal([]);

  /**
   * Apple Pay is only shown when the device supports it AND the backend
   * returns a gateway that supports it.  The documented API does not list
   * an 'apple_pay' gateway, so we check the device capability but also gate
   * on the availability of a tokenization-capable gateway (stripe / authorize_net).
   */
  isApplePayAvailable: WritableSignal<boolean> = signal(false);

  private applePayToken: WritableSignal<ApplePayTokenResult | null> =
    signal(null);
  usingApplePay = computed(() => this.applePayToken() !== null);

  private get shippingAddress(): BaseWooCommerceAddressDetails | undefined {
    const checkoutFormValues = this.checkoutForm.value;
    const shippingMethod: ShippingMethod = checkoutFormValues['shippingMethod'];

    if (shippingMethod === ShippingMethod.Pickup) {
      return checkoutFormValues['pickupAddress'];
    } else if (shippingMethod === ShippingMethod.Delivery) {
      return checkoutFormValues['deliveryAddress'];
    }

    return undefined;
  }

  private get shippingAddress$(): Observable<
    BaseWooCommerceAddressDetails | undefined
  > {
    const formValues = this.checkoutForm.value;
    const initialPickupAddress = formValues['pickupAddress'];
    const initialDeliveryAddress = formValues['deliveryAddress'];
    const initialShippingMethod = formValues['shippingMethod'];

    return combineLatest({
      pickupAddress: this.checkoutForm
        .get('pickupAddress')!
        .valueChanges.pipe(startWith(initialPickupAddress)),
      deliveryAddress: this.checkoutForm
        .get('deliveryAddress')!
        .valueChanges.pipe(startWith(initialDeliveryAddress)),
      shippingMethod: this.checkoutForm
        .get('shippingMethod')!
        .valueChanges.pipe(startWith(initialShippingMethod))
    }).pipe(
      map(({ pickupAddress, deliveryAddress, shippingMethod }) => {
        if (shippingMethod === ShippingMethod.Pickup) {
          this.store.dispatch(
            new UpsertShippingAction({
              method: shippingMethod,
              address: pickupAddress
            })
          );

          return pickupAddress;
        } else if (shippingMethod === ShippingMethod.Delivery) {
          this.store.dispatch(
            new UpsertShippingAction({
              method: shippingMethod,
              address: deliveryAddress
            })
          );

          return deliveryAddress;
        }

        return undefined;
      })
    );
  }

  private get decodedShippingAddress$() {
    return this.shippingAddress$.pipe(
      filter((address) => address != null),
      switchMap(({ label, address_1, address_2 }) => {
        const address = label || `${address_1}, ${address_2}`;

        return this.googleMapService
          .geocodeAddress(address)
          .pipe(catchError(() => of(undefined)));
      }),
      filter((decodedAddress) => decodedAddress != null)
    );
  }

  billingAddressDisplay: WritableSignal<
    BaseWooCommerceAddressDetails | undefined
  > = signal(undefined);

  constructor(
    private dialog: Dialog,
    private store: Store,
    private router: Router,
    private formBuilder: FormBuilder,
    private toastrService: ToastrService,
    private googleMapService: GoogleMapService,
    private wooCommerceCheckoutApiService: WooCommerceCheckoutApiService,
    private wooCommerceShippingService: WooCommerceShippingService,
    private wooCommerceShippingDatetimeApiService: WooCommerceShippingDatetimeApiService,
    private wooCommerceCartApiService: WooCommerceCartApiService,
    private wooCommerceCartTokenService: WooCommerceCartTokenService,
    private happyStoreSwitchService: HappyStoreSwitchService,
    private happyStoreStorageService: HappyStoreStorageService,
    private happyStoreSelectionService: HappyStoreSelectionService,
    private googleAnalyticsService: GoogleAnalyticsService,
    private tipsApiService: WooCommerceTipsApiService,
    private cloverPaymentService: CloverPaymentService,
    private customerPaymentApiService: WooCommerceCustomerPaymentApiService,
    private applePayService: ApplePayService
  ) {
    effect(() => {
      const currentStorePickupAddresses = this.currentStorePickupAddresses();
      const shippingMethod =
        this.checkoutForm?.get('shippingMethod')?.value as ShippingMethod | undefined;
      const pickupAddress = this.checkoutForm?.get('pickupAddress')
        ?.value as WooCommerceShippingAddress | null;

      if (
        shippingMethod !== ShippingMethod.Pickup ||
        currentStorePickupAddresses.length === 0 ||
        this.isPickupAddressInCurrentStore(pickupAddress)
      ) {
        return;
      }

      this.checkoutForm.patchValue({
        pickupAddress: currentStorePickupAddresses[0]
      });
    });

    // Update tip field when totals signal changes
    effect(() => {
      const fees = this.fees();
      const driverFee = this.driverFee();

      if (this.isCartLoading()) {
        return;
      }

      if (driverFee && fees.length > 0) {
        this.checkoutForm.patchValue({ tip: driverFee.totals.value });
      } else {
        this.checkoutForm.patchValue({ tip: 0 });
      }
    });

    // Update payment method validators based on cart total
    effect(() => {
      const totalPrice = parseFloat(this.totals()?.total_price || '0');
      const isFreeOrder = totalPrice === 0;
      const paymentMethodControl = this.checkoutForm.get('paymentMethod');
      const paymentDataControl = this.checkoutForm.get('paymentData');

      if (paymentMethodControl && paymentDataControl) {
        if (isFreeOrder) {
          // Remove required validators for free orders
          paymentMethodControl.removeValidators([Validators.required]);
          paymentDataControl.removeValidators([Validators.required]);
          paymentMethodControl.updateValueAndValidity();
          paymentDataControl.updateValueAndValidity();
        } else {
          // Add required validators for paid orders
          paymentMethodControl.addValidators([Validators.required]);
          paymentDataControl.addValidators([Validators.required]);
          paymentMethodControl.updateValueAndValidity();
          paymentDataControl.updateValueAndValidity();
        }
      }
    });

    // Gate Apple Pay on device support + backend having a tokenization-capable gateway
    effect(() => {
      const hasTokenGateway = this.paymentMethods().some(
        (m) => m.supports_tokenization
      );
      this.isApplePayAvailable.set(
        this.applePayService.isAvailable() && hasTokenGateway
      );
    });

    effect(() => {
      const isUpdating = this.isUpdatingShippingRate();
      const selectedShippingMethodControl = this.checkoutForm.get(
        'selectedShippingMethod'
      );

      if (selectedShippingMethodControl) {
        if (isUpdating) {
          selectedShippingMethodControl.disable({ emitEvent: false });
        } else {
          selectedShippingMethodControl.enable({ emitEvent: false });
        }
      }
    });
  }

  onSignInClick(): void {
    void this.authDialog.openSignIn();
  }

  ngOnInit() {
    this.initCheckoutForm();
    this.initMethodDetails(
      this.checkoutForm.value['shippingMethod'] === ShippingMethod.Delivery
        ? this.checkoutForm.value['deliveryAddress']
        : this.checkoutForm.value['pickupAddress'],
      this.checkoutForm.value['shippingMethod']
    );

    // Sync pending items if any, otherwise just update cart
    if (this.store.selectSnapshot(CartState.hasPendingChanges)) {
      this.shouldSyncShippingRateAfterCartSync = true;
      this.store.dispatch(new CartItemsAction.Sync());
    } else {
      this.updateCart({ forceUpdate: true });
    }

    this.subscribeToPendingChanges();
    this.subscribeToSyncCompletion();
    this.updateRestaurant();
    this.updatePaymentMethods();
    this.loadSavedCards();

    // Apple Pay availability will be updated reactively via the effect below

    // Track begin_checkout event
    this.trackBeginCheckout();

    // Start schedule expiration check
    this.startScheduleExpirationCheck();
    this.setupVisibilityChangeListener();

    // Check schedule immediately on init (in case user refreshed the page)
    this.checkScheduleExpiration();
  }

  ngOnDestroy() {
    this.stopScheduleExpirationCheck();
    this.removeVisibilityChangeListener();
    this.cloverPaymentService.destroy();
  }

  /**
   * Start periodic check for schedule expiration
   */
  private startScheduleExpirationCheck(): void {
    this.scheduleExpirationCheckId = setInterval(() => {
      this.checkScheduleExpiration();
    }, this.SCHEDULE_CHECK_INTERVAL_MS);
  }

  /**
   * Stop the schedule expiration check timer
   */
  private stopScheduleExpirationCheck(): void {
    if (this.scheduleExpirationCheckId) {
      clearInterval(this.scheduleExpirationCheckId);
      this.scheduleExpirationCheckId = null;
    }
  }

  /**
   * Setup visibility change listener to check schedule when user returns to tab
   */
  private setupVisibilityChangeListener(): void {
    this.visibilityChangeHandler = () => {
      if (document.visibilityState === 'visible') {
        this.checkScheduleExpiration();
      }
    };
    document.addEventListener('visibilitychange', this.visibilityChangeHandler);
  }

  /**
   * Remove visibility change listener
   */
  private removeVisibilityChangeListener(): void {
    if (this.visibilityChangeHandler) {
      document.removeEventListener(
        'visibilitychange',
        this.visibilityChangeHandler
      );
      this.visibilityChangeHandler = null;
    }
  }

  /**
   * Check if the current schedule has expired and reset if necessary
   */
  private checkScheduleExpiration(): void {
    const shippingState: ShippingStateModel = this.store.selectSnapshot(
      ShippingState.shippingState
    );

    // Only check if there's a schedule and it's not ASAP
    if (!shippingState.schedule) {
      return;
    }

    const schedule = shippingState.schedule;
    const scheduleTime = schedule.time;

    // If ASAP is selected, no need to validate
    if (
      typeof scheduleTime === 'string' &&
      scheduleTime.toLowerCase() === WooCommerceDatetimeTimeSlotValue.Asap
    ) {
      return;
    }

    // Get timezone from restaurant state
    const timezone = this.store.selectSnapshot(RestaurantState.timezone);

    // Check if the scheduled date/time is now in the past
    if (this.isScheduleExpired(schedule, timezone)) {
      // Reset to standard and clear schedule
      this.handleExpiredSchedule();
    }
  }

  /**
   * Check if a schedule has expired
   * @param schedule The schedule to check
   * @param timezone The restaurant's timezone
   * @returns True if the schedule is expired
   */
  private isScheduleExpired(
    schedule: ShippingScheduleStateModel,
    timezone?: string
  ): boolean {
    const scheduleDate =
      schedule.date instanceof Date ? schedule.date : new Date(schedule.date);
    const scheduleTime = schedule.time;

    // Get current time in restaurant's timezone
    const nowInTz = this.getNowInTimezone(timezone);

    // Check if the date is in the past
    const scheduleDateYear = scheduleDate.getFullYear();
    const scheduleDateMonth = scheduleDate.getMonth();
    const scheduleDateDay = scheduleDate.getDate();

    // If the date is in the past, the schedule is expired
    if (scheduleDateYear < nowInTz.year) return true;
    if (scheduleDateYear === nowInTz.year && scheduleDateMonth < nowInTz.month)
      return true;
    if (
      scheduleDateYear === nowInTz.year &&
      scheduleDateMonth === nowInTz.month &&
      scheduleDateDay < nowInTz.day
    )
      return true;

    // If the date is today, check the time
    if (
      scheduleDateYear === nowInTz.year &&
      scheduleDateMonth === nowInTz.month &&
      scheduleDateDay === nowInTz.day
    ) {
      // Check if the time is in the past
      if (scheduleTime instanceof Date) {
        const timeMinutes =
          scheduleTime.getHours() * 60 + scheduleTime.getMinutes();
        return timeMinutes <= nowInTz.totalMinutes;
      }

      if (typeof scheduleTime === 'string') {
        // Handle ASAP - never expired
        if (
          scheduleTime.toLowerCase() === WooCommerceDatetimeTimeSlotValue.Asap
        ) {
          return false;
        }

        // Parse time slot string
        const timeRange = parseTimeRange(scheduleTime);
        if (timeRange) {
          // Check if the end time is in the past
          const endMinutes = timeRange.end.milliseconds / (60 * 1000);
          return endMinutes <= nowInTz.totalMinutes;
        }
      }
    }

    return false;
  }

  /**
   * Get current date/time in the specified timezone
   * @param timezone Timezone string (e.g., "America/New_York")
   * @returns Object with date components in the timezone
   */
  private getNowInTimezone(timezone?: string): {
    year: number;
    month: number;
    day: number;
    hours: number;
    minutes: number;
    totalMinutes: number;
  } {
    const now = new Date();

    if (timezone) {
      try {
        const formatter = new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          year: 'numeric',
          month: 'numeric',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
          hour12: false
        });
        const parts = formatter.formatToParts(now);
        const year = parseInt(
          parts.find((p) => p.type === 'year')?.value || '0',
          10
        );
        const month =
          parseInt(parts.find((p) => p.type === 'month')?.value || '1', 10) - 1;
        const day = parseInt(
          parts.find((p) => p.type === 'day')?.value || '1',
          10
        );
        const hours = parseInt(
          parts.find((p) => p.type === 'hour')?.value || '0',
          10
        );
        const minutes = parseInt(
          parts.find((p) => p.type === 'minute')?.value || '0',
          10
        );

        return {
          year,
          month,
          day,
          hours,
          minutes,
          totalMinutes: hours * 60 + minutes
        };
      } catch (e) {
        console.warn('Failed to parse timezone, using local time:', e);
      }
    }

    // Fallback to local time
    return {
      year: now.getFullYear(),
      month: now.getMonth(),
      day: now.getDate(),
      hours: now.getHours(),
      minutes: now.getMinutes(),
      totalMinutes: now.getHours() * 60 + now.getMinutes()
    };
  }

  /**
   * Resolves the delivery_date string for PUT /cart.
   *  - ASAP (no schedule): today's date in the store's timezone
   *  - Scheduled: the date the user picked, extracted from the schedule object
   *
   * Returns YYYY-MM-DD format as expected by the backend.
   */
  private resolveDeliveryDate(
    schedule: ShippingScheduleStateModel | undefined
  ): string {
    if (!schedule) {
      return this.getTodayInStoreTimezone();
    }

    // Prefer the date field from the schedule (which is the actual calendar date picked)
    const scheduleDate =
      schedule.date instanceof Date ? schedule.date : new Date(schedule.date);

    // Guard against epoch / invalid dates (e.g. timestamp=0 → 1970-01-01)
    if (isNaN(scheduleDate.getTime()) || scheduleDate.getFullYear() < 2000) {
      return this.getTodayInStoreTimezone();
    }

    const yyyy = scheduleDate.getFullYear();
    const mm = String(scheduleDate.getMonth() + 1).padStart(2, '0');
    const dd = String(scheduleDate.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private resolveDeliveryTime(
    schedule: ShippingScheduleStateModel | undefined
  ): string | null {
    if (!schedule) {
      return this.getCurrentTimeInStoreTimezone();
    }

    const time = schedule.time;

    if (
      typeof time === 'string' &&
      time.toLowerCase() === WooCommerceDatetimeTimeSlotValue.Asap
    ) {
      return null;
    }

    if (time instanceof Date) {
      const hh = String(time.getHours()).padStart(2, '0');
      const min = String(time.getMinutes()).padStart(2, '0');
      return `${hh}:${min}`;
    }

    if (typeof time === 'string') {
      const isoMatch = time.match(
        /^\d{4}-\d{2}-\d{2}T(\d{2}):(\d{2}):\d{2}/
      );
      if (isoMatch) {
        return `${isoMatch[1]}:${isoMatch[2]}`;
      }
      return time;
    }

    return null;
  }

  private getCartContext(): { subtotal: number; itemCount: number } {
    const totals = this.totals();
    const minorUnit = totals?.currency_minor_unit || 2;
    const subtotal =
      parseFloat(totals?.total_items || '0') / Math.pow(10, minorUnit);
    return { subtotal, itemCount: this.cartItems().length };
  }

  private getTodayInStoreTimezone(): string {
    const timezone = this.store.selectSnapshot(RestaurantState.timezone);
    const now = this.getNowInTimezone(timezone);
    const yyyy = now.year;
    const mm = String(now.month + 1).padStart(2, '0');
    const dd = String(now.day).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  private getCurrentTimeInStoreTimezone(): string {
    const timezone = this.store.selectSnapshot(RestaurantState.timezone);
    const now = this.getNowInTimezone(timezone);
    const hh = String(now.hours).padStart(2, '0');
    const min = String(now.minutes).padStart(2, '0');
    return `${hh}:${min}`;
  }

  /**
   * Handle an expired schedule by resetting to standard and showing a warning
   */
  private handleExpiredSchedule(): void {
    // Reset the shipping method details to the default available option.
    this.checkoutForm.patchValue({
      shippingMethodDetails: this.getDefaultShippingMethodDetails(
        this.checkoutForm.get('shippingMethod')?.value as ShippingMethod | null
      )
    });

    // Clear the schedule from state
    this.store.dispatch(new UpsertShippingAction({ schedule: undefined }));

    // Show warning to user
    this.toastrService.warning(
      'Your scheduled time has passed. Please select a new time or order now.',
      'Schedule Expired'
    );
  }

  private subscribeToPendingChanges() {
    this.store
      .select(CartState.hasPendingChanges)
      .pipe(
        skip(1),
        untilDestroyed(this),
        debounceTime(500),
        filter((hasChanges) => hasChanges)
      )
      .subscribe(() => {
        this.shouldSyncShippingRateAfterCartSync = true;
        this.store.dispatch(new CartItemsAction.Sync());
      });
  }

  private subscribeToSyncCompletion() {
    this.store
      .select(CartState.isLoading('sync'))
      .pipe(
        startWith(false),
        pairwise(),
        filter(([prev, curr]) => prev && !curr),
        untilDestroyed(this)
      )
      .subscribe(() => {
        if (!this.shouldSyncShippingRateAfterCartSync) {
          return;
        }

        this.shouldSyncShippingRateAfterCartSync = false;
        this.syncCurrentShippingRate();
      });
  }

  private syncCurrentShippingRate() {
    if (this.cartItems().length === 0) {
      return;
    }

    const selectedShippingMethod = this.checkoutForm.get(
      'selectedShippingMethod'
    )?.value as CardSelectorOption<ShippingOption> | null;

    if (!selectedShippingMethod) {
      return;
    }

    this.onShippingMethodChange(selectedShippingMethod);
  }

  onMapInitialized(map: google.maps.Map) {
    this.decodedShippingAddress$.pipe(untilDestroyed(this)).subscribe({
      next: ({ location, center }: GoogleMapDecodedAddress) => {
        this.googleMapCenter = center;
        this.googleMapMarkers = [location];
      }
    });
  }

  isOrderBlocked(): boolean {
    const isStandard =
      this.checkoutForm.get('shippingMethodDetails')?.value ===
      PickupOption.standard;
    return !this.isRestaurantOpen() && isStandard;
  }

  private getDefaultShippingMethodDetails(
    shippingMethod?: ShippingMethod | null
  ): PickupOption | null {
    if (shippingMethod === ShippingMethod.Pickup && !this.isRestaurantOpen()) {
      return null;
    }

    return PickupOption.standard;
  }

  private resetShippingMethodDetailsToDefault(
    shippingMethod = this.checkoutForm.get('shippingMethod')
      ?.value as ShippingMethod | null
  ): void {
    this.checkoutForm
      .get('shippingMethodDetails')
      ?.setValue(this.getDefaultShippingMethodDetails(shippingMethod));
  }

  isUberDeliveryOutOfRange(): boolean {
    const isDeliveryMethodSelected =
      this.checkoutForm?.get('shippingMethod')?.value ===
      ShippingMethod.Delivery;
    const hasDeliveryAddress =
      !!this.checkoutForm?.get('deliveryAddress')?.value;
    const isShippingLoading =
      this.isLoadingShippingMethods() || this.isUpdatingShippingRate();

    if (!isDeliveryMethodSelected || !hasDeliveryAddress || isShippingLoading) {
      return false;
    }

    // "Out of range" only when the delivery-check returned no available delivery
    // methods at all (is_deliverable: false). A $0 cost on an Uber method means
    // the store configured free delivery — it is NOT an out-of-range signal.
    return this.availableShippingMethods().length === 0;
  }

  hasPlaceOrderError(): boolean {
    return this.isOrderBlocked() || this.isUberDeliveryOutOfRange();
  }

  getOrderBlockedMessage(): string {
    if (this.isUberDeliveryOutOfRange()) {
      return this.UBER_OUT_OF_RANGE_MESSAGE;
    }

    if (this.isTodayHoliday()) {
      return 'Restaurant is closed for holiday. Please schedule your order for a later date.';
    }
    return 'Restaurant is currently closed. Please schedule your order or try again later.';
  }

  async onPlaceOrderClick(): Promise<void> {
    if (this.isOrderBlocked()) {
      return;
    }

    if (this.isUberDeliveryOutOfRange()) {
      this.toastrService.error(this.UBER_OUT_OF_RANGE_MESSAGE);
      return;
    }

    if (this.checkoutForm.invalid) {
      this.checkoutForm.markAllAsTouched();
    }

    if (this.cartItems().length === 0) {
      this.toastrService.error('Cart is empty!');
      return;
    }

    if (!this.shippingAddress) {
      this.toastrService.error('Select Shipping Address!');
      return;
    }

    const checkoutFormValues = this.checkoutForm.value;
    if (!checkoutFormValues['shippingMethodDetails']) {
      this.toastrService.error('Select Delivery Method!');
      return;
    }

    const shippingState: ShippingStateModel = this.store.selectSnapshot(
      ShippingState.shippingState
    );

    // Validate schedule before placing order
    if (shippingState.schedule) {
      const timezone = this.store.selectSnapshot(RestaurantState.timezone);
      if (this.isScheduleExpired(shippingState.schedule, timezone)) {
        this.handleExpiredSchedule();
        return;
      }

      const unavailableItems = this.getUnavailableCartItemsForSchedule(
        shippingState.schedule,
        timezone
      );
      if (unavailableItems.length > 0) {
        const unavailableItemNames = unavailableItems
          .map((item) => convertHtml2Text(item.name))
          .join(', ');
        this.toastrService.error(
          `Some items are not available at the scheduled time: ${unavailableItemNames}. Please reschedule your order.`,
          'Items Unavailable'
        );
        return;
      }
    }

    if (
      !checkoutFormValues['dropoff'] &&
      shippingState.method === ShippingMethod.Delivery
    ) {
      this.toastrService.error('Enter Dropoff Options!');
      return;
    }

    if (!checkoutFormValues['first_name'] || !checkoutFormValues['last_name']) {
      this.toastrService.error('Enter Your Name!');
      return;
    }

    if (!checkoutFormValues['phone']) {
      this.toastrService.error('Enter Phone Number!');
      return;
    }

    if (!checkoutFormValues['email']) {
      this.toastrService.error('Enter Valid Email!');
      return;
    }

    // Check if order total is $0 (free order)
    const isFreeOrder = parseFloat(this.totals()?.total_price || '0') === 0;

    // Skip payment validation for free orders, saved card payments, and Apple Pay
    if (!isFreeOrder && !this.usingSavedCard() && !this.usingApplePay()) {
      if (!checkoutFormValues['paymentMethod']) {
        this.toastrService.error('Select Payment Method!');
        return;
      }

      // Validate billing address only if card payment is selected
      if (this.isCardPayment()) {
        if (!checkoutFormValues['billing_address_1']) {
          this.toastrService.error('Enter Billing Address!');
          return;
        }

        if (!checkoutFormValues['billing_city']) {
          this.toastrService.error('Enter Billing City!');
          return;
        }

        if (!checkoutFormValues['billing_state']) {
          this.toastrService.error('Enter Billing State!');
          return;
        }

        if (!checkoutFormValues['billing_postcode']) {
          this.toastrService.error('Enter Billing Zip Code!');
          return;
        }

        // Only validate billing fields if not using same as contact details
        const useSameAsContactDetails =
          checkoutFormValues['useSameAsContactDetails'];
        if (!useSameAsContactDetails) {
          if (
            !checkoutFormValues['billing_first_name'] ||
            !checkoutFormValues['billing_last_name']
          ) {
            this.toastrService.error('Enter Billing Name!');
            return;
          }
        }
      }

      // Validate payment fields if payment method is expanded (skip for saved cards)
      if (this.expandedPaymentMethodId() && !this.usingSavedCard()) {
        const paymentFieldsForm = this.checkoutForm.get(
          'paymentFields'
        ) as FormRecord;
        if (paymentFieldsForm && paymentFieldsForm.invalid) {
          paymentFieldsForm.markAllAsTouched();
          this.toastrService.error(
            'Please fill in all payment fields correctly!'
          );
          return;
        }
      }
    }

    const isCardPaymentSelected = this.isCardPayment();
    const useSameAsContactDetails =
      checkoutFormValues['useSameAsContactDetails'];

    // Prepare billing address - use contact details if card payment not selected or if checkbox is checked
    const billingAddressData =
      isCardPaymentSelected && !isFreeOrder
        ? {
            first_name: useSameAsContactDetails
              ? checkoutFormValues['first_name']
              : checkoutFormValues['billing_first_name'],
            last_name: useSameAsContactDetails
              ? checkoutFormValues['last_name']
              : checkoutFormValues['billing_last_name'],
            email: checkoutFormValues['email'],
            phone: checkoutFormValues['phone'],
            address_1: checkoutFormValues['billing_address_1'] || '',
            address_2: '',
            city: checkoutFormValues['billing_city'] || '',
            state: checkoutFormValues['billing_state'] || '',
            postcode: checkoutFormValues['billing_postcode'] || '',
            country: this.shippingAddress?.country || 'US',
            address: (() => {
              const parts: string[] = [];
              const address1 = (
                checkoutFormValues['billing_address_1'] || ''
              ).trim();
              const city = (checkoutFormValues['billing_city'] || '').trim();
              const state = (checkoutFormValues['billing_state'] || '').trim();
              const postcode = (
                checkoutFormValues['billing_postcode'] || ''
              ).trim();
              const country = (this.shippingAddress?.country || 'US').trim();

              const stateZip =
                state && postcode
                  ? `${state} ${postcode}`
                  : state || postcode || '';

              if (address1) parts.push(address1);
              if (city) parts.push(city);
              if (stateZip) parts.push(stateZip);
              if (country) parts.push(country);

              return parts.join(', ') || '';
            })()
          }
        : {
            first_name: checkoutFormValues['first_name'],
            last_name: checkoutFormValues['last_name'],
            email: checkoutFormValues['email'],
            phone: checkoutFormValues['phone'],
            address_1: this.shippingAddress?.address_1 || '',
            address_2: this.shippingAddress?.address_2 || '',
            city: this.shippingAddress?.city || '',
            state: this.shippingAddress?.state || '',
            postcode: this.shippingAddress?.postcode || '',
            country: this.shippingAddress?.country || '',
            address:
              this.shippingAddress?.address || this.shippingAddress?.label || ''
          };

    const savedCard = this.selectedSavedCard();
    const applePayResult = this.applePayToken();
    let cloverToken: string | null = null;
    let stripePaymentMethodId: string | null = null;
    let stripeSavedTokenId: number | null = null;
    let applePayTokenData: any | null = null;

    // Resolve gateway — saved card carries its own gateway_id
    const resolvedGateway: HappyPaymentGateway = savedCard
      ? (savedCard.gateway_id as HappyPaymentGateway)
      : (this.selectedPaymentGateway() || 'stripe');

    const resolvedPaymentMethod =
      resolvedGateway === 'paypal'
        ? 'digital_wallet'
        : resolvedGateway === 'cash'
          ? 'cash'
          : 'credit_card';

    if (applePayResult && !isFreeOrder) {
      applePayTokenData = applePayResult.token;
    } else if (
      checkoutFormValues['paymentMethod']?.includes('clover') &&
      this.cloverPayment
    ) {
      this.isSubmitLoading.set(true);
      try {
        const result = await this.cloverPayment.createToken();
        if (result.errors) {
          const errorMessage =
            result.errors['message'] ||
            Object.values(result.errors)[0] ||
            'Failed to securely process payment details.';
          this.toastrService.error(errorMessage);
          this.isSubmitLoading.set(false);
          return;
        }
        cloverToken = result.token ?? (result as any).id ?? null;
        if (!cloverToken) {
          this.toastrService.error(
            'Failed to obtain payment token. Please try again.'
          );
          this.isSubmitLoading.set(false);
          return;
        }
      } catch (err) {
        this.toastrService.error(
          'Payment tokenization failed. Please try again.'
        );
        this.isSubmitLoading.set(false);
        return;
      }
    } else if (
      resolvedGateway === 'stripe' &&
      !savedCard &&
      !isFreeOrder &&
      this.stripeCardElement
    ) {
      this.isSubmitLoading.set(true);
      try {
        stripePaymentMethodId = await this.stripeCardElement.createPaymentMethod();

        // Cart-Subs.md §11 Stripe flow: save the PM so backend creates
        // Stripe Customer + attaches PM, then checkout uses saved token.
        if (this.isAuthenticated() && stripePaymentMethodId) {
          const saveResponse = await firstValueFrom(
            this.customerPaymentApiService.addCustomerPaymentMethod({
              token: stripePaymentMethodId,
              gateway_id: 'stripe'
            })
          );

          if (saveResponse?.data?.payment_method?.id) {
            stripeSavedTokenId = saveResponse.data.payment_method.id;
          }
        }
      } catch (err: any) {
        this.toastrService.error(
          err?.message || 'Failed to process card. Please try again.'
        );
        this.isSubmitLoading.set(false);
        return;
      }
    }

    // ── Subscription guards (Cart-Subs.md §7, §16) ──
    const cartHasForcedSub = this.hasForcedSubscription();
    const cartHasSubOption = this.hasSubscriptionOption();
    const isSubscriptionCheckout = cartHasForcedSub || cartHasSubOption;

    if (isSubscriptionCheckout && !this.isAuthenticated()) {
      this.toastrService.error(
        'Subscriptions require an account. Please log in or sign up first.'
      );
      return;
    }

    if (isSubscriptionCheckout && resolvedGateway === 'paypal') {
      this.toastrService.error(
        'PayPal is not available for subscription orders. Please select a credit card payment method.'
      );
      return;
    }

    // ── Build checkout payload per Cart-Subs.md §11 ──
    const checkoutParams: CheckoutOrderParams = {
      gateway: resolvedGateway,
      paymentMethod: resolvedPaymentMethod,
      isZeroTotal: isFreeOrder
    };

    if (isFreeOrder) {
      // Zero-total: no gateway, no payment data needed
    } else if (savedCard) {
      checkoutParams.paymentTokenId = savedCard.id;
    } else if (cloverToken) {
      checkoutParams.paymentMethodData = { token: cloverToken };
    } else if (applePayTokenData) {
      checkoutParams.paymentMethodData = { apple_pay_token: applePayTokenData };
    } else if (stripeSavedTokenId) {
      checkoutParams.paymentTokenId = stripeSavedTokenId;
    } else if (stripePaymentMethodId) {
      checkoutParams.paymentMethodData = { payment_method: stripePaymentMethodId };
    } else if (resolvedGateway === 'cash') {
      // Cash: no payment data needed, just gateway + payment_method
    } else {
      // New card (AuthNet) or PayPal — extract payment_method_data from form fields
      const paymentFieldsForm = this.checkoutForm.get('paymentFields') as FormRecord;
      const fieldData: Record<string, string> = {};
      if (paymentFieldsForm) {
        Object.keys(paymentFieldsForm.controls).forEach((key) => {
          let value: string = paymentFieldsForm.get(key)?.value || '';
          if (key === 'card_number') {
            value = value.replace(/\D/g, '');
          }
          fieldData[key] = value;
        });
      }
      checkoutParams.paymentMethodData = Object.keys(fieldData).length > 0
        ? fieldData
        : {};
    }

    if (isSubscriptionCheckout) {
      checkoutParams.isSubscription = true;
      // Frequency values will come from the cart's meal plan config;
      // the backend resolves defaults, but we pass them if known.
    }

    // ── order_processing_type for instant_ordering stores ──
    const storeType = this.store.selectSnapshot(RestaurantState.storeType);
    if (storeType === 'instant_ordering') {
      const methodDetails = checkoutFormValues['shippingMethodDetails'];
      checkoutParams.orderProcessingType =
        methodDetails === PickupOption.schedule ? 'schedule' : 'instant';
    }

    // ── Sequential pre-submit: persist → validate → checkout ──
    this.isSubmitLoading.set(true);
    this.persistAndCheckout(
      checkoutFormValues,
      billingAddressData,
      shippingState,
      checkoutParams,
      cloverToken,
      applePayTokenData
    );
  }

  /**
   * Sequential pre-submit pipeline per Cart-Subs.md §5:
   *  1. PUT /cart (persist contact info, delivery date, notes)
   *  2. PUT /cart/address (shipping + billing)
   *  3. POST /cart/validate (pre-checkout validation)
   *  4. POST /cart/checkout (place order)
   *
   * Each step must complete before the next starts. If validation fails,
   * we stop and show errors to the user.
   */
  private persistAndCheckout(
    formValues: Record<string, any>,
    billingAddressData: any,
    shippingState: ShippingStateModel,
    checkoutParams: CheckoutOrderParams,
    cloverToken: string | null,
    applePayTokenData: any | null
  ): void {
    const shippingAddr = this.shippingAddress;

    // Step 1: Persist contact info + delivery date + notes onto cart
    // ASAP → today's date in the store's timezone; Scheduled → the selected date
    const deliveryDate = this.resolveDeliveryDate(shippingState.schedule);
    const deliveryTime = this.resolveDeliveryTime(shippingState.schedule);

    const isPickup = shippingState.method === ShippingMethod.Pickup;
    const stateAddr = shippingState.address;
    const pickupLocationId =
      isPickup && stateAddr && 'id' in stateAddr
        ? Number(
            getPickupLocationBackendId(stateAddr as WooCommerceShippingAddress)
          ) || null
        : null;

    const updateCart$ = this.wooCommerceCartApiService.updateCart({
      pickup_location_id: pickupLocationId,
      delivery_date: deliveryDate,
      delivery_time: deliveryTime,
      contact_first_name: formValues['first_name'] || '',
      contact_last_name: formValues['last_name'] || '',
      contact_email: formValues['email'] || '',
      contact_phone: formValues['phone'] || '',
      keep_me_updated: false,
      customer_note: formValues['note'] || null,
      food_note: null
    });

    // Step 2: Set addresses on cart (both shipping and billing in parallel).
    const addressOps: Observable<void>[] = [];
    if (shippingAddr) {
      const shippingAddressPayload: HappyCartAddressRequest = {
        type: 'shipping',
        first_name: formValues['first_name'],
        last_name: formValues['last_name'],
        email: formValues['email'],
        phone: formValues['phone'],
        address_1: shippingAddr.address_1,
        address_2: shippingAddr.address_2,
        city: shippingAddr.city,
        state_code: shippingAddr.state,
        postcode: shippingAddr.postcode,
        country_code: shippingAddr.country || 'US'
      };

      addressOps.push(
        this.wooCommerceCartApiService.setCartAddress(shippingAddressPayload)
      );
    }
    if (billingAddressData) {
      addressOps.push(
        this.wooCommerceCartApiService.setCartAddress({
          type: 'billing',
          first_name: billingAddressData.first_name,
          last_name: billingAddressData.last_name,
          email: billingAddressData.email,
          phone: billingAddressData.phone,
          address_1: billingAddressData.address_1,
          city: billingAddressData.city,
          state_code: billingAddressData.state,
          postcode: billingAddressData.postcode,
          country_code: billingAddressData.country || 'US'
        })
      );
    }
    const setAddresses$ = addressOps.length > 0
      ? forkJoin(addressOps)
      : of(undefined);

    // Step 3: Validate → Step 4: Checkout
    updateCart$
      .pipe(
        switchMap(() => setAddresses$),
        switchMap(() => this.wooCommerceCartApiService.validateCart()),
        switchMap((validation: HappyCartValidateResponse) => {
          const isValid = validation.valid ?? validation.is_valid ?? true;
          if (!isValid) {
            const errors = validation.errors || [];
            errors.forEach((e) => {
              this.toastrService.error(e.message, 'Validation');
            });
            if (errors.length === 0) {
              this.toastrService.error('Cart validation failed. Please review your order.');
            }
            this.isSubmitLoading.set(false);
            return of(null);
          }

          if (validation.warnings?.length) {
            validation.warnings.forEach((w) => {
              this.toastrService.warning(w.message, 'Warning');
            });
          }

          if (validation.auto_removals?.length) {
            validation.auto_removals.forEach((r) => {
              this.toastrService.warning(r.reason, 'Item Removed');
            });
            this.store.dispatch(new CartItemsAction.Get({ forceUpdate: true }));
          }

          return this.wooCommerceCheckoutApiService.createOrder(checkoutParams);
        }),
        filter((order): order is WooCommerceCheckoutResponse => order !== null),
        switchMap((createdOrder) => {
          if (cloverToken) {
            return this.chargeCloverPayment(createdOrder, cloverToken).pipe(
              map(() => createdOrder)
            );
          }
          if (applePayTokenData) {
            return this.chargeApplePayPayment(
              createdOrder,
              applePayTokenData
            ).pipe(map(() => createdOrder));
          }
          return of(createdOrder);
        }),
        finalize(() => this.isSubmitLoading.set(false)),
        untilDestroyed(this)
      )
      .subscribe({
        next: (order: WooCommerceCheckoutResponse) => {
          this.toastrService.success(
            'Your order id: ' + order.order_id,
            'Order created!'
          );
          this.trackPurchaseEvent(order);
          this.clearCartAndRefreshToken();
          this.saveToLocalStorage(order);
          this.store.dispatch(new AddOrderAction(order));
          this.applePayToken.set(null);
          this.router.navigate([`/orders/${order.order_id}/success`]);
        },
        error: (err) => {
          if (err instanceof CloverChargeError) {
            this.toastrService.error(
              'Payment charge failed. Please contact support with order #' +
                err.orderId
            );
          } else if (err instanceof ApplePayChargeError) {
            this.toastrService.error(
              'Apple Pay charge failed. Please contact support with order #' +
                err.orderId
            );
          } else {
            this.toastrService.error(
              err?.message || 'Something went wrong. Please try again.'
            );
          }
        }
      });
  }

  handleRemoveCoupon(coupon: WooCommerceCoupon) {
    this.store
      .dispatch(new CartCouponAction.Remove(coupon))
      .pipe(untilDestroyed(this))
      .subscribe({
        next: () => {
          this.toastrService.success(``, 'Coupon removed!');
          this.store.dispatch(new CartItemsAction.Get());
        }
      });
  }

  onPaymentMethodClick(paymentMethod: WooCommercePaymentMethod) {
    const { id, fields, gateway } = paymentMethod;
    const needsExpansion =
      (fields || []).length > 0 || id.includes('clover') || gateway === 'stripe';

    if (!needsExpansion) {
      this.checkoutForm.patchValue({ paymentMethod: id, paymentData: {} });
      this.expandedPaymentMethodId.set(null);

      // Track payment info selection
      this.trackAddPaymentInfo(paymentMethod.title || id);

      return;
    }

    // Toggle expansion: if already expanded, collapse; otherwise expand
    if (this.expandedPaymentMethodId() === id) {
      this.expandedPaymentMethodId.set(null);
      // Clear payment data when collapsing
      this.checkoutForm.patchValue({ paymentMethod: null, paymentData: null });
      this.clearPaymentFieldsForm();
    } else {
      // Collapse previously expanded payment method if any
      if (this.expandedPaymentMethodId()) {
        this.clearPaymentFieldsForm();
      }

      this.expandedPaymentMethodId.set(id);
      if ((fields || []).length > 0) {
        this.initPaymentFieldsForm(fields);
      } else {
        // Clover might not have fields but we want it expanded
        this.checkoutForm.patchValue({ paymentData: {} });
      }
      this.checkoutForm.patchValue({ paymentMethod: id });

      // Track payment info selection
      this.trackAddPaymentInfo(paymentMethod.title || id);
    }
  }

  // Requests
  private updateCart(params?: CartItemsActionGetPayload) {
    return this.store.dispatch(new CartItemsAction.Get(params));
  }

  private clearCartAndRefreshToken(): void {
    this.wooCommerceCartTokenService.refreshCartToken().subscribe({
      next: () => this.store.dispatch(new CartItemsAction.Clear())
    });
  }

  private updateRestaurant() {
    return this.store.dispatch([
      new GetRestaurantGeneralAction(),
      new GetPickupLocationsAction()
    ]);
  }

  private ensureProductDataForCategoryAvailability(): Observable<void> {
    const hasProducts =
      Object.keys(this.store.selectSnapshot(RestaurantState.products) || {})
        .length > 0;
    const hasCategories =
      Object.keys(
        this.store.selectSnapshot(RestaurantState.productCategories) || {}
      ).length > 0;

    if (hasProducts && hasCategories) {
      return of(void 0);
    }

    const ensureCategories$ = hasCategories
      ? of(void 0)
      : this.store
          .dispatch(new GetRestaurantProductCategoriesAction())
          .pipe(map(() => void 0));

    return ensureCategories$.pipe(
      switchMap(() => {
        if (hasProducts) {
          return of(void 0);
        }

        return this.store
          .dispatch(new GetRestaurantProductsAction())
          .pipe(map(() => void 0));
      }),
      catchError(() => of(void 0))
    );
  }

  private getCartCategories() {
    const cartItems = this.store.selectSnapshot(CartState.items);
    const products = this.store.selectSnapshot(RestaurantState.products);
    const productCategories = this.store.selectSnapshot(
      RestaurantState.productCategories
    );
    return resolveCartCategories(cartItems, products, productCategories);
  }

  private getUnavailableCartItemsForSchedule(
    schedule: ShippingScheduleStateModel,
    timezone?: string
  ) {
    const scheduleDate =
      schedule.date instanceof Date ? schedule.date : new Date(schedule.date);
    const scheduleTime =
      schedule.time instanceof Date ? schedule.time : String(schedule.time);
    const cartItems = this.store.selectSnapshot(CartState.items);
    const products = this.store.selectSnapshot(RestaurantState.products);
    const productCategories = this.store.selectSnapshot(
      RestaurantState.productCategories
    );
    return getUnavailableCartItemsForSchedule(
      cartItems,
      products,
      productCategories,
      scheduleDate,
      scheduleTime,
      timezone
    );
  }

  private updatePaymentMethods() {
    return this.store.dispatch(new GetRestaurantPaymentMethodsAction());
  }

  private loadSavedCards(): void {
    if (!this.isAuthenticated()) return;

    this.savedCardsLoading.set(true);
    this.customerPaymentApiService
      .getCustomerPaymentMethods()
      .pipe(
        take(1),
        finalize(() => this.savedCardsLoading.set(false))
      )
      .subscribe({
        next: (response) => {
          const cards = response.data?.payment_methods ?? [];
          this.savedCards.set(cards);
          const defaultCard = cards.find((c) => c.is_default) ?? cards[0];
          if (defaultCard) {
            this.onSavedCardSelect(defaultCard);
          }
        }
      });
  }

  onSavedCardSelect(card: PaymentMethodResponse): void {
    if (this.selectedSavedCard()?.id === card.id) return;
    this.selectedSavedCard.set(card);
    this.expandedPaymentMethodId.set(null);
    this.clearPaymentFieldsForm();
  }

  useNewCard(): void {
    this.selectedSavedCard.set(null);
  }

  /**
   * Handle successful Apple Pay token from the Apple Pay button component.
   * Stores the token and sets Apple Pay as the selected payment method.
   */
  onApplePayTokenReceived(result: ApplePayTokenResult): void {
    this.applePayToken.set(result);
    // Clear other payment selections
    this.selectedSavedCard.set(null);
    this.expandedPaymentMethodId.set(null);
    this.clearPaymentFieldsForm();
    // Set Apple Pay as the payment method in the form
    this.checkoutForm.patchValue({
      paymentMethod: 'apple_pay',
      paymentData: [{ key: 'apple_pay_token', value: 'pending' }]
    });

    this.trackAddPaymentInfo('Apple Pay');
  }

  /**
   * Handle Apple Pay errors from the button component.
   */
  onApplePayError(errorMessage: string): void {
    this.applePayToken.set(null);
    this.toastrService.error(errorMessage);
  }

  /**
   * Clear Apple Pay selection and allow choosing another payment method.
   */
  clearApplePay(): void {
    this.applePayToken.set(null);
    this.checkoutForm.patchValue({
      paymentMethod: null,
      paymentData: null
    });
  }

  getCardBrandName(type: string): string {
    const names: Record<string, string> = {
      visa: 'Visa',
      mastercard: 'Mastercard',
      amex: 'American Express',
      americanexpress: 'American Express',
      discover: 'Discover'
    };
    return names[type?.toLowerCase()] || type || 'Card';
  }

  private selectCartShippingRateById(
    rateId: string | undefined,
    address: BaseWooCommerceAddressDetails | undefined,
    shippingMethod: ShippingMethod,
    packageId: string = '0'
  ): Observable<WooCommerceCartInfo | void | null> {
    if (!rateId || !address) {
      return of(null);
    }

    if (shippingMethod === ShippingMethod.Pickup) {
      const pickupLocationId =
        'id' in address
          ? Number(getPickupLocationBackendId(address as WooCommerceShippingAddress)) ||
            null
          : null;

      if (!pickupLocationId) {
        return of(null);
      }

      return this.wooCommerceCartApiService.setCartShipping({
        pickup_location_id: pickupLocationId
      });
    }

    return this.wooCommerceCartApiService.selectShippingRate(
      rateId,
      address,
      packageId
    );
  }

  onNoteClick(): void {
    const initialValue: string = this.checkoutForm.get('note')!.value || '';
    const dialogRef: DialogRef<string, CheckoutNotesDialogComponent> =
      this.dialog.open(CheckoutNotesDialogComponent, {
        data: { title: 'Delivery Note', note: initialValue },
        width: '100%',
        maxWidth: '550px'
      });

    dialogRef.closed.pipe(filter((note) => note != null)).subscribe({
      next: (note: string) => this.checkoutForm.patchValue({ note })
    });
  }

  onDropoffClick(): void {
    const initialValue: CheckoutDropoffDialogResponse | null =
      this.checkoutForm.get('dropoff')!.value;
    const dialogRef: DialogRef<
      CheckoutDropoffDialogResponse,
      CheckoutDropoffDialogComponent
    > = this.dialog.open(CheckoutDropoffDialogComponent, {
      data: { title: 'Dropoff Options', dropoff: initialValue },
      width: '100%',
      maxWidth: '550px'
    });

    dialogRef.closed.pipe(filter((dropoff) => dropoff != null)).subscribe({
      next: (dropoff: CheckoutDropoffDialogResponse) =>
        this.checkoutForm.patchValue({ dropoff })
    });
  }

  handleRemoveTip() {
    this.isApplyingTip.set(true);

    this.tipsApiService
      .removeTipFromCart()
      .pipe(
        untilDestroyed(this),
        finalize(() => this.isApplyingTip.set(false))
      )
      .subscribe({
        next: () => {
          this.updateCart({ forceUpdate: true });
          this.toastrService.success(``, 'Tip has been removed!');
        }
      });
  }

  onShippingMethodChange(
    cardOption: CardSelectorOption<ShippingOption> | null
  ): void {
    const selectedMethod = cardOption?.data || undefined;
    const shippingMethod = this.checkoutForm.get('shippingMethod')?.value;

    this.store.dispatch(
      new UpsertShippingAction({ selectedShippingMethod: selectedMethod })
    );

    // Track shipping info selection
    if (selectedMethod) {
      this.trackAddShippingInfo(
        selectedMethod.name || String(selectedMethod.id)
      );
    }

    const rateId = String(selectedMethod?.id);
    const address =
      shippingMethod === ShippingMethod.Delivery
        ? this.checkoutForm.get('deliveryAddress')?.value
        : this.checkoutForm.get('pickupAddress')?.value;

    if (rateId == null || address == null) {
      return;
    }

    this.isUpdatingShippingRate.set(true);
    this.selectCartShippingRateById(rateId, address, shippingMethod)
      .pipe(
        finalize(() => this.isUpdatingShippingRate.set(false)),
        untilDestroyed(this)
      )
      .subscribe({
        next: () => {
          this.updateCart({ forceUpdate: true });
          // Case B: PUT /cart/shipping just completed — if contact info is
          // already filled, fire PUT /cart now so the backend can create
          // provider quotes (e.g. Uber) before the user reaches checkout.
          this.triggerCartContactInfoUpdate()
            .pipe(untilDestroyed(this))
            .subscribe();
        },
        error: () => {
          this.toastrService.error(
            'Failed to update shipping method. Please try again.'
          );
        }
      });
  }

  /**
   * Build a PUT /v1/tenant/cart/address payload (type=shipping) from a
   * just-confirmed delivery address. Contact info is pulled from the
   * current checkout form — fields the customer hasn't filled yet are
   * left undefined (the API accepts them as nullable).
   */
  private buildCartShippingAddressPayload(
    address: BaseWooCommerceAddressDetails
  ): HappyCartAddressRequest {
    const formValues = this.checkoutForm.value;
    return {
      type: 'shipping',
      first_name: formValues['first_name'] || undefined,
      last_name: formValues['last_name'] || undefined,
      email: formValues['email'] || undefined,
      phone: formValues['phone'] || undefined,
      address_1: address.address_1,
      address_2: address.address_2,
      city: address.city,
      state_code: address.state,
      postcode: address.postcode,
      country_code: address.country || 'US'
    };
  }

  /**
   * Build a PUT /v1/tenant/cart/address payload (type=billing) from a
   * just-confirmed billing address. Contact info prefers the dedicated
   * billing form fields, falling back to the main contact fields.
   */
  private buildCartBillingAddressPayload(
    address: BaseWooCommerceAddressDetails
  ): HappyCartAddressRequest {
    const formValues = this.checkoutForm.value;
    return {
      type: 'billing',
      first_name:
        formValues['billing_first_name'] ||
        formValues['first_name'] ||
        undefined,
      last_name:
        formValues['billing_last_name'] ||
        formValues['last_name'] ||
        undefined,
      email: formValues['email'] || undefined,
      phone: formValues['phone'] || undefined,
      address_1: address.address_1,
      address_2: address.address_2,
      city: address.city,
      state_code: address.state,
      postcode: address.postcode,
      country_code: address.country || 'US'
    };
  }

  onPickupAddressClick() {
    const dialogRef: DialogRef<
      CheckoutSelectAddressDialogResponse,
      CheckoutDeliveryAddressDialogComponent
    > = this.dialog.open(CheckoutDeliveryAddressDialogComponent, {
      data: {
        title: 'Pickup Address',
        shippingMethod: this.checkoutForm.get('shippingMethod')
          ?.value as ShippingMethod,
        addressList: this.pickupAddresses(),
        editable: false
      },
      width: '100%',
      minHeight: '265px',
      maxWidth: '550px'
    });

    dialogRef.closed
      .pipe(
        filter((address) => address != null),
        switchMap((response: CheckoutSelectAddressDialogResponse) => {
          const selectedAddress =
            response.selectedAddress as WooCommerceShippingAddress;

          return this.happyStoreSwitchService
            .confirmPickupLocationStore(selectedAddress)
            .pipe(
              switchMap((switchResult) => {
                if (switchResult === 'cancelled') {
                  return EMPTY;
                }

                if (switchResult === 'switching') {
                  return EMPTY;
                }

                const pickupAddress =
                  this.pickupAddresses().find(
                    (address) => address.id === selectedAddress.id
                  ) || selectedAddress;
                const pickupLocationId =
                  Number(getPickupLocationBackendId(pickupAddress)) || null;

                // Reset shipping method details when address changes.
                this.checkoutForm.patchValue({
                  pickupAddress,
                  shippingMethodDetails: this.getDefaultShippingMethodDetails(
                    ShippingMethod.Pickup
                  )
                });

                // Clear schedule from state
                this.store.dispatch(
                  new UpsertShippingAction({ schedule: undefined })
                );

                if (!pickupLocationId) {
                  return this.wooCommerceShippingService.getShippingMethods({
                    ...pickupAddress,
                    address: pickupAddress?.address_1
                  });
                }

                return this.wooCommerceCartApiService
                  .setCartShipping({
                    pickup_location_id: pickupLocationId
                  })
                  .pipe(
                    switchMap(() =>
                      this.wooCommerceShippingService.getShippingMethods({
                        ...pickupAddress,
                        address: pickupAddress?.address_1
                      })
                    )
                  );
              })
            );
        })
      )
      .subscribe({
        next: (shippingMethods: WooCommerceShippingMethods) => {
          const pickupMethods = shippingMethods.pickupMethods;
          const selectedMethod = findFirstEligibleMethod(pickupMethods);

          this.store.dispatch(
            new SetAvailableShippingMethodsAction({
              availableShippingMethods: pickupMethods,
              selectedShippingMethod: selectedMethod
            })
          );

          // Update form control with the selected method (as card option)
          const cardOption = selectedMethod
            ? mapShippingOptionsToCardOptions([selectedMethod])[0]
            : null;
          this.checkoutForm.patchValue(
            {
              selectedShippingMethod: cardOption
            },
            { emitEvent: false }
          );

          if (selectedMethod) {
            this.trackAddShippingInfo(
              selectedMethod.name || String(selectedMethod.id)
            );
          }

          this.updateCart({ forceUpdate: true });
        },
        error: () => {
          this.toastrService.error(
            'Failed to update pickup location. Please try again.'
          );
        }
      });
  }

  private initMethodDetails(
    address: BaseWooCommerceAddressDetails,
    shippingMethod: ShippingMethod,
    isShippingMethodChanged = false
  ) {
    if (!address) {
      return;
    }

    this.isLoadingShippingMethods.set(true);

    const pickupLocationId =
      shippingMethod === ShippingMethod.Pickup && 'id' in address
        ? Number(getPickupLocationBackendId(address as WooCommerceShippingAddress)) ||
          null
        : null;

    const setShipping$: Observable<void> = pickupLocationId
      ? this.wooCommerceCartApiService.setCartShipping({
          pickup_location_id: pickupLocationId
        })
      : of(void 0 as void);

    const methods$ =
      shippingMethod === ShippingMethod.Delivery
        ? this.wooCommerceShippingService.checkDeliveryAvailability(address)
        : setShipping$.pipe(
            switchMap(() =>
              this.wooCommerceShippingService.getShippingMethods({
                ...address,
                address: address?.address_1
              })
            )
          );

    methods$.pipe(
        finalize(() => this.isLoadingShippingMethods.set(false)),
        untilDestroyed(this)
      )
      .subscribe({
        next: (shippingMethods: WooCommerceShippingMethods) => {
          // Only set delivery methods if shipping method is Delivery
          if (shippingMethod === ShippingMethod.Delivery) {
            const deliveryMethods = shippingMethods.deliveryMethods;
            const { subtotal, itemCount } = this.getCartContext();

            // Get current selected method from state
            const currentSelectedMethod = this.store.selectSnapshot(
              ShippingState.selectedShippingMethod
            );

            const firstEligible = findFirstEligibleMethod(
              deliveryMethods,
              subtotal,
              itemCount
            );
            const selectedMethod = isShippingMethodChanged
              ? firstEligible
              : currentSelectedMethod || firstEligible;

            this.store.dispatch(
              new SetAvailableShippingMethodsAction({
                availableShippingMethods: deliveryMethods,
                selectedShippingMethod: selectedMethod
              })
            );

            // Update form control with the selected method (as card option)
            const cardOption = selectedMethod
              ? mapShippingOptionsToCardOptions(
                  [selectedMethod],
                  subtotal,
                  itemCount
                )[0]
              : null;
            this.checkoutForm.patchValue(
              {
                selectedShippingMethod: cardOption
              },
              { emitEvent: false }
            );

            if (isShippingMethodChanged && cardOption) {
              this.syncCurrentShippingRate();
            }
          } else {
            const pickupMethods = shippingMethods.pickupMethods;
            const selectedMethod = findFirstEligibleMethod(pickupMethods);

            this.store.dispatch(
              new SetAvailableShippingMethodsAction({
                availableShippingMethods: pickupMethods,
                selectedShippingMethod: selectedMethod
              })
            );

            // Update form control with the selected method (as card option)
            const cardOption = selectedMethod
              ? mapShippingOptionsToCardOptions([selectedMethod])[0]
              : null;
            this.checkoutForm.patchValue(
              {
                selectedShippingMethod: cardOption
              },
              { emitEvent: false }
            );

            if (cardOption) {
              this.updateCart({ forceUpdate: true });
            }
          }
        }
      });
  }

  onDeliveryAddressClick(): void {
    const dialogRef: DialogRef<
      CheckoutSelectAddressDialogResponse,
      CheckoutDeliveryAddressDialogComponent
    > = this.dialog.open(CheckoutDeliveryAddressDialogComponent, {
      data: {
        title: 'Delivery Address',
        shippingMethod: this.checkoutForm.get('shippingMethod')
          ?.value as ShippingMethod,
        addressList: this.deliveryAddresses(),
        editable: true
      },
      width: '100%',
      minHeight: '265px',
      maxWidth: '550px'
    });

    dialogRef.closed
      .pipe(
        filter((address) => address != null),
        switchMap((response: CheckoutSelectAddressDialogResponse) => {
          const selectedAddress =
            response.selectedAddress as WooCommerceDeliveryAddress;
          const addressList =
            response.addressList as WooCommerceDeliveryAddress[];

          this.store.dispatch(new SetDeliveryAddresses(addressList));

          // Reset shipping method details when address changes.
          this.checkoutForm.patchValue({
            deliveryAddress: selectedAddress,
            shippingMethodDetails: this.getDefaultShippingMethodDetails(
              ShippingMethod.Delivery
            )
          });

          // Clear schedule from state
          this.store.dispatch(
            new UpsertShippingAction({ schedule: undefined })
          );

          this.isLoadingShippingMethods.set(true);

          // Persist the new shipping address on the cart immediately
          // (PUT /v1/tenant/cart/address) so the backend can use it for
          // delivery zone matching and live shipping quotes. The address
          // will be re-asserted at submit time so this is best-effort —
          // swallow errors so the user still sees shipping options.
          const cartAddress = this.buildCartShippingAddressPayload(selectedAddress);
          const persistAddress$ = this.wooCommerceCartApiService
            .setCartAddress(cartAddress)
            .pipe(catchError(() => of(void 0)));

          return forkJoin({
            _persist: persistAddress$,
            shippingMethods: this.wooCommerceShippingService
              .checkDeliveryAvailability(selectedAddress)
          }).pipe(
            map(({ shippingMethods }) => shippingMethods),
            finalize(() => this.isLoadingShippingMethods.set(false))
          );
        })
      )
      .subscribe({
        next: (shippingMethods: WooCommerceShippingMethods) => {
          const deliveryMethods = shippingMethods.deliveryMethods;
          const { subtotal, itemCount } = this.getCartContext();
          const selectedMethod = findFirstEligibleMethod(
            deliveryMethods,
            subtotal,
            itemCount
          );

          this.store.dispatch(
            new SetAvailableShippingMethodsAction({
              availableShippingMethods: deliveryMethods,
              selectedShippingMethod: selectedMethod
            })
          );

          // Update form control with the selected method (as card option)
          const cardOption = selectedMethod
            ? mapShippingOptionsToCardOptions(
                [selectedMethod],
                subtotal,
                itemCount
              )[0]
            : null;
          this.checkoutForm.patchValue(
            {
              selectedShippingMethod: cardOption
            },
            { emitEvent: false }
          );
          this.syncCurrentShippingRate();
        }
      });
  }

  onBillingAddressClick(): void {
    const dialogRef: DialogRef<
      CheckoutSelectAddressDialogResponse,
      CheckoutDeliveryAddressDialogComponent
    > = this.dialog.open(CheckoutDeliveryAddressDialogComponent, {
      data: {
        title: 'Billing Address',
        shippingMethod: ShippingMethod.Pickup, // Hack to skip delivery check
        checkDelivery: false,
        addressList: this.billingAddresses(),
        editable: true
      },
      width: '100%',
      minHeight: '265px',
      maxWidth: '550px'
    });

    dialogRef.closed
      .pipe(
        filter((response) => response != null),
        switchMap((response: CheckoutSelectAddressDialogResponse) => {
          const selectedAddress =
            response.selectedAddress as WooCommerceDeliveryAddress;
          const addressList =
            response.addressList as WooCommerceDeliveryAddress[];

          this.billingAddresses.set(addressList);

          this.checkoutForm.patchValue({
            billing_address_1: selectedAddress.address_1,
            billing_city: selectedAddress.city,
            billing_state: selectedAddress.state,
            billing_postcode: selectedAddress.postcode
          });

          // Persist the new billing address on the cart immediately
          // (PUT /v1/tenant/cart/address with type=billing). Best-effort —
          // the address is also re-asserted in the submit pipeline.
            const address = this.buildCartBillingAddressPayload(selectedAddress)
          return this.wooCommerceCartApiService
            .setCartAddress(address)
            .pipe(catchError(() => of(void 0)));
        }),
        untilDestroyed(this)
      )
      .subscribe();
  }

  private loadFromLocalStorage() {
    try {
      // Load saved delivery address
      const savedAddress =
        this.happyStoreStorageService.getLocalItem('saved_delivery_address');
      if (savedAddress) {
        const address = JSON.parse(savedAddress);
        if (this.deliveryAddresses().length === 0) {
          this.store.dispatch(new SetDeliveryAddresses([address]));
          if (!this.checkoutForm.get('deliveryAddress')?.value) {
            this.checkoutForm.patchValue({ deliveryAddress: address });
          }
        }
      }

      // Load saved contact info
      const savedContact =
        this.happyStoreStorageService.getLocalItem('saved_contact_info');
      if (savedContact) {
        const contact = JSON.parse(savedContact);
        const formValues = this.checkoutForm.value;
        const patch: any = {};

        if (!formValues?.['first_name']) patch.first_name = contact.first_name;
        if (!formValues?.['last_name']) patch.last_name = contact.last_name;
        if (!formValues?.['email']) patch.email = contact.email;
        if (!formValues?.['phone']) patch.phone = contact.phone;

        if (Object.keys(patch).length > 0) {
          this.checkoutForm.patchValue(patch);
        }
      }
    } catch (e) {
      console.error('Failed to load from localStorage', e);
    }
  }

  private saveToLocalStorage(order: any) {
    try {
      if (order.shipping_address) {
        // Save contact info
        const contactInfo = {
          first_name: order.shipping_address.first_name,
          last_name: order.shipping_address.last_name,
          email: order.shipping_address.email,
          phone: order.shipping_address.phone
        };
        this.happyStoreStorageService.setLocalItem(
          'saved_contact_info',
          JSON.stringify(contactInfo)
        );

        // Save delivery address (if it was a delivery order)
        const shippingState = this.store.selectSnapshot(
          ShippingState.shippingState
        );
        if (
          shippingState.method === ShippingMethod.Delivery &&
          shippingState.address
        ) {
          this.happyStoreStorageService.setLocalItem(
            'saved_delivery_address',
            JSON.stringify(shippingState.address)
          );
        }
      }
    } catch (e) {
      console.error('Failed to save to localStorage', e);
    }
  }

  private initPaymentFieldsForm(fields: WooCommercePaymentField[] = []) {
    // Clear existing payment fields if any
    this.clearPaymentFieldsForm();

    // Create new form record for payment fields
    const paymentFieldsForm = this.formBuilder.record({});

    fields.forEach((field: WooCommercePaymentField) => {
      const validators: ValidatorFn[] =
        this.getValidatorsForPaymentField(field);
      const initialValue = this.getPaymentFieldInitialValue(field);

      paymentFieldsForm.addControl(
        field.name,
        this.formBuilder.control(initialValue, validators)
      );
    });

    // Add payment fields as a nested form group in checkout form
    this.checkoutForm.addControl('paymentFields', paymentFieldsForm);

    // Subscribe to form changes to update payment data
    paymentFieldsForm.valueChanges.pipe(untilDestroyed(this)).subscribe({
      next: (formValues) => {
        const expandedId = this.expandedPaymentMethodId();
        if (expandedId && paymentFieldsForm.valid) {
          const paymentData = this.form2PaymentData(
            formValues as Record<string, string>
          );
          this.checkoutForm.patchValue({ paymentData }, { emitEvent: false });
        } else if (expandedId) {
          // Clear payment data if form is invalid
          this.checkoutForm.patchValue(
            { paymentData: null },
            { emitEvent: false }
          );
        }
      }
    });
  }

  private clearPaymentFieldsForm() {
    if (this.checkoutForm.get('paymentFields')) {
      this.checkoutForm.removeControl('paymentFields');
    }
  }

  private form2PaymentData(
    form: Record<string, unknown>
  ): { key: string; value: string }[] {
    return Object.entries(form).map(([key, value]) => ({
      key,
      value: value == null ? '' : String(value)
    }));
  }

  private getPaymentFieldInitialValue(
    field: WooCommercePaymentField
  ): string | boolean | null {
    if (field.type === 'checkbox') {
      return field.value === 'true';
    }

    return field.value ?? null;
  }

  private getValidatorsForPaymentField(
    field: WooCommercePaymentField
  ): ValidatorFn[] {
    const validators: ValidatorFn[] = [Validators.required];

    // Add specific validators based on field name
    const fieldName = field.name.toLowerCase();

    // Card number validation
    if (fieldName.includes('number') || fieldName.includes('card_number')) {
      validators.push(CreditCardValidators.number);
    }
    // Expiration date validation (MM/YYYY format)
    else if (fieldName.includes('exp')) {
      validators.push(CreditCardValidators.expDate);
    }
    // CVV/CVC validation (3-4 digits)
    else if (
      fieldName.includes('cvv') ||
      fieldName.includes('cvc') ||
      fieldName.includes('card_code')
    ) {
      validators.push(CreditCardValidators.cvc);
    }

    return validators;
  }

  private updateBillingAddressValidators(): void {
    const isCardPaymentSelected = this.isCardPayment();
    const billingAddress1Control = this.checkoutForm.get('billing_address_1');
    const billingCityControl = this.checkoutForm.get('billing_city');
    const billingStateControl = this.checkoutForm.get('billing_state');
    const billingPostcodeControl = this.checkoutForm.get('billing_postcode');
    const billingFirstNameControl = this.checkoutForm.get('billing_first_name');
    const billingLastNameControl = this.checkoutForm.get('billing_last_name');
    const billingEmailControl = this.checkoutForm.get('billing_email');
    const billingPhoneControl = this.checkoutForm.get('billing_phone');

    if (
      !billingAddress1Control ||
      !billingCityControl ||
      !billingStateControl ||
      !billingPostcodeControl ||
      !billingFirstNameControl ||
      !billingLastNameControl ||
      !billingEmailControl ||
      !billingPhoneControl
    ) {
      return;
    }

    if (isCardPaymentSelected) {
      // Add required validators for card payments
      billingAddress1Control.addValidators([
        Validators.required,
        Validators.minLength(3)
      ]);
      billingCityControl.addValidators([
        Validators.required,
        Validators.minLength(2)
      ]);
      billingStateControl.addValidators([
        Validators.required,
        Validators.pattern(/^[A-Za-z]{2}$/)
      ]);
      billingPostcodeControl.addValidators([
        Validators.required,
        CreditCardValidators.zipCode
      ]);

      // Only add validators to name if not using same as contact details
      // Email and phone are optional (only format validation if provided)
      const useSameAsContactDetails = this.checkoutForm.get(
        'useSameAsContactDetails'
      )?.value;
      if (!useSameAsContactDetails) {
        billingFirstNameControl.addValidators([Validators.required]);
        billingLastNameControl.addValidators([Validators.required]);
        // Email format validation only (optional)
        billingEmailControl.addValidators([Validators.email]);
        // Phone format validation only (optional)
        billingPhoneControl.addValidators([PhoneValidators.US_PhoneNumber]);
      }
    } else {
      // Remove validators when not a card payment
      billingAddress1Control.removeValidators([
        Validators.required,
        Validators.minLength(3)
      ]);
      billingCityControl.removeValidators([
        Validators.required,
        Validators.minLength(2)
      ]);
      billingStateControl.removeValidators([
        Validators.required,
        Validators.pattern(/^[A-Za-z]{2}$/)
      ]);
      billingPostcodeControl.removeValidators([
        Validators.required,
        CreditCardValidators.zipCode
      ]);
      billingFirstNameControl.removeValidators([Validators.required]);
      billingLastNameControl.removeValidators([Validators.required]);
      billingEmailControl.removeValidators([Validators.email]);
      billingPhoneControl.removeValidators([PhoneValidators.US_PhoneNumber]);
    }

    // Update validity
    billingAddress1Control.updateValueAndValidity({ emitEvent: false });
    billingCityControl.updateValueAndValidity({ emitEvent: false });
    billingStateControl.updateValueAndValidity({ emitEvent: false });
    billingPostcodeControl.updateValueAndValidity({ emitEvent: false });
    billingFirstNameControl.updateValueAndValidity({ emitEvent: false });
    billingLastNameControl.updateValueAndValidity({ emitEvent: false });
    billingEmailControl.updateValueAndValidity({ emitEvent: false });
    billingPhoneControl.updateValueAndValidity({ emitEvent: false });
  }

  // Form Init
  private initCheckoutForm() {
    this.checkoutForm = this.formBuilder.record({
      first_name: [null, [Validators.required]],
      last_name: [null, [Validators.required]],
      email: [null, [Validators.required, Validators.email]],
      phone: [null, [Validators.required, PhoneValidators.US_PhoneNumber]],
      deliveryAddress: [null, [Validators.required]],
      pickupAddress: [null, [Validators.required]],
      dropoff: [null, [Validators.required]],
      shippingMethod: [null, [Validators.required]],
      shippingMethodDetails: [PickupOption.standard, [Validators.required]],
      selectedShippingMethod: [null],
      paymentMethod: [null, [Validators.required]],
      paymentData: [null, [Validators.required]],
      note: [null, [Validators.required]],
      tip: 0,
      // Billing Address fields (required only for card payments)
      billing_address_1: [null],
      billing_city: [null],
      billing_state: [null],
      billing_postcode: [null],
      billing_first_name: [null],
      billing_last_name: [null],
      billing_email: [null],
      billing_phone: [null],
      useSameAsContactDetails: [false]
    });

    this.updateInitialCheckoutForm();
    this.loadFromLocalStorage();
    this.subscribeToFormChanges();
    // Initialize selected payment method signal
    const initialPaymentMethod = this.checkoutForm.get('paymentMethod')?.value;
    this.selectedPaymentMethodId.set(initialPaymentMethod);
    // Initialize billing address validators based on initial payment method
    this.updateBillingAddressValidators();
  }

  private updateInitialCheckoutForm() {
    const shippingState: ShippingStateModel = this.store.selectSnapshot(
      ShippingState.shippingState
    );

    const initialShippingMethod: ShippingMethod =
      shippingState.method || ShippingMethod.Pickup;

    const shippingStatePickupAddress =
      initialShippingMethod === ShippingMethod.Pickup &&
      shippingState.address &&
      this.isPickupAddressInCurrentStore(
        shippingState.address as WooCommerceShippingAddress
      )
        ? shippingState.address
        : null;

    const initialPickupAddress: BaseWooCommerceAddressDetails =
      shippingStatePickupAddress
        ? shippingStatePickupAddress
        : this.currentStorePickupAddresses()[0] || this.pickupAddresses()[0];

    const initialDeliveryAddress: BaseWooCommerceAddressDetails | undefined =
      initialShippingMethod === ShippingMethod.Delivery
        ? shippingState.address!
        : this.deliveryAddresses().find(
            (deliveryAddress) => deliveryAddress.shipping_methods.length > 1
          );

    const initialShippingMethodDetails: PickupOption | null =
      shippingState.schedule == null
        ? this.getDefaultShippingMethodDetails(initialShippingMethod)
        : PickupOption.schedule;

    const initialCardOption = shippingState.selectedShippingMethod
      ? mapShippingOptionsToCardOptions([
          shippingState.selectedShippingMethod
        ])[0]
      : null;

    this.checkoutForm.patchValue({
      shippingMethod: initialShippingMethod,
      shippingMethodDetails: initialShippingMethodDetails,
      deliveryAddress: initialDeliveryAddress,
      pickupAddress: initialPickupAddress,
      selectedShippingMethod: initialCardOption
    });

    if (initialShippingMethod === ShippingMethod.Delivery) {
      this.checkoutForm.get('note')!.addValidators([Validators.required]);
    } else if (initialShippingMethod === ShippingMethod.Pickup) {
      this.checkoutForm.get('note')!.removeValidators([Validators.required]);
    }
  }

  private isPickupAddressInCurrentStore(
    address?: WooCommerceShippingAddress | null
  ): boolean {
    if (!address) {
      return false;
    }

    const selectedStore = this.happyStoreSelectionService.getSelected();
    if (!selectedStore) {
      return true;
    }

    return (
      address.storeApiKey === selectedStore.storeApiKey &&
      address.tenant === selectedStore.tenant
    );
  }

  private subscribeToFormChanges() {
    // TODO: Refactor
    this.checkoutForm
      .get('deliveryAddress')!
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (address) => {
          // Reset shipping method details when address changes.
          this.checkoutForm.patchValue({
            shippingMethodDetails: this.getDefaultShippingMethodDetails(
              ShippingMethod.Pickup
            )
          });

          this.store.dispatch(
            new UpsertShippingAction({
              method: ShippingMethod.Delivery,
              address,
              schedule: undefined
            })
          );
        }
      });

    this.checkoutForm
      .get('pickupAddress')!
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (address) => {
          // Reset shipping method details when address changes.
          this.checkoutForm.patchValue({
            shippingMethodDetails: this.getDefaultShippingMethodDetails(
              ShippingMethod.Delivery
            )
          });

          this.store.dispatch(
            new UpsertShippingAction({
              method: ShippingMethod.Pickup,
              address,
              schedule: undefined
            })
          );
        }
      });

    this.checkoutForm
      .get('shippingMethod')!
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (method) => {
          // Reset shipping method details when switching between delivery/pickup.
          this.checkoutForm.patchValue({
            shippingMethodDetails: this.getDefaultShippingMethodDetails(method)
          });

          if (method === ShippingMethod.Pickup) {
            const pickupAddresses = this.currentStorePickupAddresses();
            if (pickupAddresses.length === 1) {
              this.checkoutForm.patchValue(
                { pickupAddress: pickupAddresses[0] },
                { emitEvent: false }
              );
            }
          }

          // TODO: Refactor (When user selects other shippingMethod, we should update this one)
          this.initMethodDetails(
            method === ShippingMethod.Delivery
              ? this.checkoutForm.value['deliveryAddress']
              : this.checkoutForm.value['pickupAddress'],
            method,
            true
          );

          this.store.dispatch(
            new UpsertShippingAction({
              method,
              methodDetails: undefined,
              schedule: undefined,
              address:
                method === ShippingMethod.Delivery
                  ? this.checkoutForm.value['deliveryAddress']
                  : this.checkoutForm.value['pickupAddress']
            })
          );

          if (method === ShippingMethod.Delivery) {
            this.checkoutForm.get('note')!.addValidators([Validators.required]);
          } else if (method === ShippingMethod.Pickup) {
            this.checkoutForm
              .get('note')!
              .removeValidators([Validators.required]);
          }
        }
      });

    this.checkoutForm
      .get('phone')!
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (phone) => {
          if (typeof phone !== 'string') {
            return;
          }

          const normalizedPhone = normalizeUSPhoneNumber(phone);
          if (normalizedPhone !== phone) {
            this.checkoutForm.patchValue(
              { phone: normalizedPhone },
              { emitEvent: false }
            );
          }
        }
      });

    this.checkoutForm
      .get('shippingMethodDetails')!
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (methodDetails) => {
          if (methodDetails === PickupOption.schedule) {
            const formValues = this.checkoutForm.value;
            this.openScheduleLaterDialog(
              formValues['shippingMethod'],
              formValues['deliveryAddress'],
              formValues['pickupAddress']
            );

            this.store.dispatch(new UpsertShippingAction({ methodDetails }));
          } else if (methodDetails === PickupOption.standard) {
            this.store.dispatch(
              new UpsertShippingAction({ methodDetails, schedule: undefined })
            );
          } else {
            this.store.dispatch(
              new UpsertShippingAction({
                methodDetails: undefined,
                schedule: undefined
              })
            );
          }
        }
      });

    this.checkoutForm
      .get('selectedShippingMethod')!
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (shippingMethod) => {
          if (this.cartItems().length > 0) {
            this.onShippingMethodChange(shippingMethod);
          }
        }
      });

    // Update billing address validators when payment method changes
    this.checkoutForm
      .get('paymentMethod')!
      .valueChanges.pipe(
        startWith(this.checkoutForm.get('paymentMethod')?.value),
        untilDestroyed(this)
      )
      .subscribe({
        next: (paymentMethodId) => {
          this.selectedPaymentMethodId.set(paymentMethodId);
          this.updateBillingAddressValidators();
        }
      });

    // Sync billing address with contact details when checkbox is checked
    this.checkoutForm
      .get('useSameAsContactDetails')!
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe({
        next: (useSame) => {
          const billingFirstNameControl =
            this.checkoutForm.get('billing_first_name')!;
          const billingLastNameControl =
            this.checkoutForm.get('billing_last_name')!;
          const billingEmailControl = this.checkoutForm.get('billing_email')!;
          const billingPhoneControl = this.checkoutForm.get('billing_phone')!;

          if (useSame) {
            const formValues = this.checkoutForm.value;
            // Sync values
            this.checkoutForm.patchValue(
              {
                billing_first_name: formValues['first_name'],
                billing_last_name: formValues['last_name'],
                billing_email: formValues['email'],
                billing_phone: formValues['phone']
              },
              { emitEvent: false }
            );

            // Remove validators when using same as contact details
            billingFirstNameControl.removeValidators([Validators.required]);
            billingLastNameControl.removeValidators([Validators.required]);
            billingEmailControl.removeValidators([Validators.email]);
            billingPhoneControl.removeValidators([
              PhoneValidators.US_PhoneNumber
            ]);
          } else {
            // Add validators back when not using same as contact details
            // Email and phone are optional (only format validation if provided)
            billingFirstNameControl.addValidators([Validators.required]);
            billingLastNameControl.addValidators([Validators.required]);
            billingEmailControl.addValidators([Validators.email]);
            billingPhoneControl.addValidators([PhoneValidators.US_PhoneNumber]);
          }

          // Update validity
          billingFirstNameControl.updateValueAndValidity({ emitEvent: false });
          billingLastNameControl.updateValueAndValidity({ emitEvent: false });
          billingEmailControl.updateValueAndValidity({ emitEvent: false });
          billingPhoneControl.updateValueAndValidity({ emitEvent: false });
        }
      });

    // Sync billing address when contact details change and checkbox is checked
    combineLatest([
      this.checkoutForm
        .get('first_name')!
        .valueChanges.pipe(
          startWith(this.checkoutForm.get('first_name')!.value)
        ),
      this.checkoutForm
        .get('last_name')!
        .valueChanges.pipe(
          startWith(this.checkoutForm.get('last_name')!.value)
        ),
      this.checkoutForm
        .get('email')!
        .valueChanges.pipe(startWith(this.checkoutForm.get('email')!.value)),
      this.checkoutForm
        .get('phone')!
        .valueChanges.pipe(startWith(this.checkoutForm.get('phone')!.value)),
      this.checkoutForm
        .get('useSameAsContactDetails')!
        .valueChanges.pipe(
          startWith(this.checkoutForm.get('useSameAsContactDetails')!.value)
        )
    ])
      .pipe(untilDestroyed(this))
      .subscribe({
        next: ([first_name, last_name, email, phone, useSame]) => {
          if (useSame) {
            this.checkoutForm.patchValue(
              {
                billing_first_name: first_name,
                billing_last_name: last_name,
                billing_email: email,
                billing_phone: phone
              },
              { emitEvent: false }
            );
          }
        }
      });

    // Update billing address display
    combineLatest([
      this.checkoutForm
        .get('billing_address_1')!
        .valueChanges.pipe(
          startWith(this.checkoutForm.get('billing_address_1')?.value)
        ),
      this.checkoutForm
        .get('billing_city')!
        .valueChanges.pipe(
          startWith(this.checkoutForm.get('billing_city')?.value)
        ),
      this.checkoutForm
        .get('billing_state')!
        .valueChanges.pipe(
          startWith(this.checkoutForm.get('billing_state')?.value)
        ),
      this.checkoutForm
        .get('billing_postcode')!
        .valueChanges.pipe(
          startWith(this.checkoutForm.get('billing_postcode')?.value)
        )
    ])
      .pipe(untilDestroyed(this))
      .subscribe({
        next: ([address_1, city, state, postcode]) => {
          if (!address_1) {
            this.billingAddressDisplay.set(undefined);
          } else {
            this.billingAddressDisplay.set({
              address_1,
              address_2: '',
              city,
              state,
              postcode,
              country: 'US'
            });
          }
        }
      });

    // Refresh shipping methods when cart items are added
    this.store
      .select(CartState.items)
      .pipe(
        startWith([]), // Start with empty array to enable pairwise
        pairwise(), // Get previous and current values
        filter(([prev, curr]) => curr.length > prev.length), // Only when items are added
        untilDestroyed(this)
      )
      .subscribe({
        next: ([_, items]) => {
          // Only refresh shipping methods if cart has items and we have an address
          if (items.length > 0) {
            const shippingMethod = this.checkoutForm.value['shippingMethod'];
            const address =
              shippingMethod === ShippingMethod.Delivery
                ? this.checkoutForm.value['deliveryAddress']
                : this.checkoutForm.value['pickupAddress'];

            if (address) {
              const methods$ =
                shippingMethod === ShippingMethod.Delivery
                  ? this.wooCommerceShippingService.checkDeliveryAvailability(
                      address
                    )
                  : this.wooCommerceShippingService.getShippingMethods({
                      ...address,
                      address: address?.address_1
                    });

              methods$.pipe(untilDestroyed(this))
                .subscribe({
                  next: (shippingMethods: WooCommerceShippingMethods) => {
                    const methods =
                      shippingMethod === ShippingMethod.Delivery
                        ? shippingMethods.deliveryMethods
                        : shippingMethods.pickupMethods;

                    const currentSelected = this.store.selectSnapshot(
                      ShippingState.selectedShippingMethod
                    );
                    const { subtotal, itemCount } = this.getCartContext();

                    // Keep current selection if still available, otherwise select first eligible
                    const selectedMethod =
                      methods.find((m) => m.id === currentSelected?.id) ||
                      findFirstEligibleMethod(methods, subtotal, itemCount);

                    this.store.dispatch(
                      new SetAvailableShippingMethodsAction({
                        availableShippingMethods: methods,
                        selectedShippingMethod: selectedMethod
                      })
                    );

                    // Automatically select the shipping rate in cart
                    if (selectedMethod) {
                      const cardOption = mapShippingOptionsToCardOptions([
                        selectedMethod
                      ])[0];

                      // Update form control
                      this.checkoutForm.patchValue(
                        {
                          selectedShippingMethod: cardOption
                        },
                        { emitEvent: false }
                      );
                    }
                  }
                });
            }
          }
        }
      });

    // Case A: contact info changes AFTER shipping zone is already confirmed.
    // Uses a snapshot guard so this never races against PUT /cart/shipping.
    combineLatest([
      this.checkoutForm
        .get('first_name')!
        .valueChanges.pipe(
          startWith(this.checkoutForm.get('first_name')!.value)
        ),
      this.checkoutForm
        .get('last_name')!
        .valueChanges.pipe(
          startWith(this.checkoutForm.get('last_name')!.value)
        ),
      this.checkoutForm
        .get('email')!
        .valueChanges.pipe(startWith(this.checkoutForm.get('email')!.value)),
      this.checkoutForm
        .get('phone')!
        .valueChanges.pipe(startWith(this.checkoutForm.get('phone')!.value)),
      this.store.select(ShippingState.schedule)
    ])
      .pipe(
        debounceTime(500),
        filter(([firstName, lastName, email, phone]) =>
          !!firstName && !!lastName && !!email && !!phone &&
          !!this.store.selectSnapshot(ShippingState.selectedShippingMethod)
        ),
        switchMap(() => this.triggerCartContactInfoUpdate()),
        untilDestroyed(this)
      )
      .subscribe();
  }

  /**
   * Calls PUT /cart and PUT /cart/address with the current contact info and
   * delivery date so the backend can create provider quotes (e.g. Uber) in
   * the background. Must only be called after PUT /cart/shipping has completed.
   */
  private triggerCartContactInfoUpdate(): Observable<void | undefined> {
    const formValues = this.checkoutForm.value;
    const firstName: string = formValues['first_name'] || '';
    const lastName: string = formValues['last_name'] || '';
    const email: string = formValues['email'] || '';
    const phone: string = formValues['phone'] || '';

    if (!firstName || !lastName || !email || !phone) {
      return of(undefined);
    }

    const shippingState = this.store.selectSnapshot(ShippingState.shippingState);
    const deliveryDate = this.resolveDeliveryDate(shippingState.schedule);
    const deliveryTime = this.resolveDeliveryTime(shippingState.schedule);
    const isPickup = shippingState.method === ShippingMethod.Pickup;
    const stateAddr = shippingState.address;
    const pickupLocationId =
      isPickup && stateAddr && 'id' in stateAddr
        ? Number(
            getPickupLocationBackendId(stateAddr as WooCommerceShippingAddress)
          ) || null
        : null;

    const updateCart$ = this.wooCommerceCartApiService
      .updateCart({
        pickup_location_id: pickupLocationId,
        delivery_date: deliveryDate,
        delivery_time: deliveryTime,
        contact_first_name: firstName,
        contact_last_name: lastName,
        contact_email: email,
        contact_phone: phone,
        keep_me_updated: false,
        customer_note: formValues['note'] || null,
        food_note: null
      })
      .pipe(catchError(() => of(undefined)));

    // Also update the shipping address with the latest email/phone so the
    // backend has it on the address record (required for Uber quote creation).
    const deliveryAddr = !isPickup ? stateAddr as BaseWooCommerceAddressDetails | undefined : undefined;
    const updateAddress$ = deliveryAddr
      ? this.wooCommerceCartApiService
          .setCartAddress({
            type: 'shipping',
            first_name: firstName,
            last_name: lastName,
            email,
            phone,
            address_1: deliveryAddr.address_1,
            address_2: deliveryAddr.address_2,
            city: deliveryAddr.city,
            state_code: deliveryAddr.state,
            postcode: deliveryAddr.postcode,
            country_code: deliveryAddr.country || 'US'
          })
          .pipe(catchError(() => of(undefined)))
      : of(undefined);

    return updateCart$.pipe(switchMap(() => updateAddress$));
  }

  private openScheduleLaterDialog(
    shippingMethod: ShippingMethod = this.checkoutForm.value['shippingMethod'],
    deliveryAddress: WooCommerceDeliveryAddress = this.checkoutForm.value[
      'deliveryAddress'
    ],
    pickupAddress: WooCommerceShippingAddress = this.checkoutForm.value[
      'pickupAddress'
    ]
  ) {
    if (shippingMethod === ShippingMethod.Pickup) {
      this.isShippingDateTimesLoading.set(true);
      this.wooCommerceShippingDatetimeApiService
        .getShippingDatetime(getPickupLocationBackendId(pickupAddress))
        .pipe(
          finalize(() => this.isShippingDateTimesLoading.set(false)),
          untilDestroyed(this)
        )
        .subscribe({
          next: (response: WooCommercePickupLocationDatetimeResponse) => {
            if (this.hasAvailableSlots(response)) {
              this.openDialogInternal(shippingMethod, {
                delivery_dates: response.delivery_dates
              });
            } else {
              this.toastrService.error('No pickup schedules available!');
              this.resetShippingMethodDetailsToDefault(shippingMethod);
            }
          },
          error: () => {
            this.toastrService.error('Failed to load pickup schedules');
            this.resetShippingMethodDetailsToDefault(shippingMethod);
          }
        });
      return;
    } else if (shippingMethod === ShippingMethod.Delivery) {
      const selectedMethod = this.store.selectSnapshot(
        ShippingState.selectedShippingMethod
      );
      const methodId = selectedMethod
        ? String(selectedMethod.id).split(':')[0]
        : null;

      if (!methodId) {
        this.toastrService.error('No delivery method selected!');
        this.resetShippingMethodDetailsToDefault(shippingMethod);
        return;
      }

      this.isShippingDateTimesLoading.set(true);
      this.wooCommerceShippingDatetimeApiService
        .getDeliveryDates(methodId)
        .pipe(
          finalize(() => this.isShippingDateTimesLoading.set(false)),
          untilDestroyed(this)
        )
        .subscribe({
          next: (response: WooCommercePickupLocationDatetimeResponse) => {
            if (
              response.delivery_dates &&
              response.delivery_dates.length > 0
            ) {
              this.openDialogInternal(shippingMethod, {
                delivery_dates: response.delivery_dates
              });
            } else {
              this.toastrService.error('No delivery schedules available!');
              this.resetShippingMethodDetailsToDefault(shippingMethod);
            }
          },
          error: () => {
            this.toastrService.error('Failed to load delivery schedules');
            this.resetShippingMethodDetailsToDefault(shippingMethod);
          }
        });
    }
  }

  private openDialogInternal(
    shippingMethod: ShippingMethod,
    datetimeSettings: any
  ) {
    if (datetimeSettings.delivery_dates.length === 0) {
      this.toastrService.error('No delivery dates available!');
      this.resetShippingMethodDetailsToDefault(shippingMethod);
      this.store.dispatch(new UpsertShippingAction({ schedule: undefined }));

      return;
    }

    if (datetimeSettings) {
      const confirmMessage =
        shippingMethod === ShippingMethod.Pickup
          ? 'Do you want to schedule a pickup for later?'
          : 'Do you want to schedule a delivery for later?';

      const confirmDialogRef: DialogRef<boolean, ConfirmDialogComponent> =
        this.dialog.open(ConfirmDialogComponent, {
          data: {
            title: 'Schedule Order',
            message: confirmMessage
          }
        });

      confirmDialogRef.closed.pipe(take(1)).subscribe({
        next: (confirmed: boolean | undefined) => {
          if (!confirmed) {
            this.resetShippingMethodDetailsToDefault(shippingMethod);
            this.store.dispatch(
              new UpsertShippingAction({ schedule: undefined })
            );
            return;
          }

          const scheduleTitle =
            shippingMethod === ShippingMethod.Pickup
              ? 'Schedule Food Pickup'
              : 'Schedule Food Delivery';

          // Get existing schedule from state to preserve user's selection
          const shippingState: ShippingStateModel = this.store.selectSnapshot(
            ShippingState.shippingState
          );
          const existingSchedule = shippingState.schedule;

          // Get holidays, timezone, and working hours for date filtering
          const holidays = this.store.selectSnapshot(RestaurantState.holidays);
          const timezone = this.store.selectSnapshot(RestaurantState.timezone);
          const workingHours = this.store.selectSnapshot(
            RestaurantState.workingHours
          );
          this.ensureProductDataForCategoryAvailability()
            .pipe(take(1))
            .subscribe(() => {
              const cartCategories = this.getCartCategories();
              if (this.cartItems().length > 0 && cartCategories.length === 0) {
                this.toastrService.error(
                  'Unable to validate category availability for your cart. Please refresh and try again.'
                );
                this.resetShippingMethodDetailsToDefault(shippingMethod);
                return;
              }

              // TODO: Refactor to smaller
              const dialogRef: DialogRef<
                DateTimeDialogData,
                DateTimeDialogComponent
              > = this.dialog.open(DateTimeDialogComponent, {
                data: {
                  title: scheduleTitle,
                  datetimeSettings,
                  workingHours,
                  existingSchedule,
                  holidays,
                  timezone,
                  cartCategories
                },
                width: '100%',
                maxWidth: '550px'
              });

              dialogRef.closed.pipe(take(1)).subscribe({
                next: (datetime?: DateTimeDialogData) => {
                  if (!datetime) {
                    this.resetShippingMethodDetailsToDefault(shippingMethod);
                    return;
                  }
                  const unavailableItems =
                    this.getUnavailableCartItemsForSchedule(datetime, timezone);
                  if (unavailableItems.length === 0) {
                    this.store.dispatch(
                      new UpsertShippingAction({ schedule: datetime })
                    );
                    return;
                  }

                  const rescheduleDialogRef: DialogRef<
                    boolean,
                    ConfirmDialogComponent
                  > = this.dialog.open(ConfirmDialogComponent, {
                    data: {
                      title: 'Update scheduled order?',
                      message:
                        'Some products in your cart are not available in the selected time range. If you continue, those products will be removed from your cart.',
                      cancelLabel: 'Cancel',
                      confirmLabel: 'Reschedule'
                    }
                  });

                  rescheduleDialogRef.closed.pipe(take(1)).subscribe({
                    next: (confirmed) => {
                      if (!confirmed) {
                        return;
                      }

                      const removeActions = unavailableItems.map(
                        (item) => new CartItemsAction.Remove(item.key)
                      );
                      this.store.dispatch([
                        ...removeActions,
                        new UpsertShippingAction({ schedule: datetime })
                      ]);
                    }
                  });
                }
              });
            });
        }
      });
    } else {
      const errorMessage =
        shippingMethod === ShippingMethod.Pickup
          ? 'Scheduling an order is not possible for this pickup option!'
          : 'Scheduling an order is not possible for this delivery method!';

      this.toastrService.error(errorMessage);
      this.resetShippingMethodDetailsToDefault(shippingMethod);
    }
  }

  private chargeCloverPayment(
    order: WooCommerceCheckoutResponse,
    token: string
  ): Observable<unknown> {
    return this.cloverPaymentService.chargeOrder(order.order_id, token);
  }

  private chargeApplePayPayment(
    order: WooCommerceCheckoutResponse,
    token: any
  ): Observable<unknown> {
    return this.applePayService.chargeOrder(order.order_id, token);
  }

  /**
   * Track begin_checkout event in Google Analytics
   */
  private trackBeginCheckout(): void {
    try {
      const cartItems = this.cartItems();
      const totals = this.totals();
      const coupons = this.coupons();

      if (cartItems.length === 0) {
        return;
      }

      const currencyDivider = 10 ** (totals.currency_minor_unit || 2);

      const items: GA4Item[] = cartItems.map((item, index) => ({
        item_id: item.id.toString(),
        item_name: convertHtml2Text(item.name),
        price: parseFloat(item.prices.price) / currencyDivider,
        quantity: item.quantity,
        index,
        item_variant:
          item.variation
            ?.map((v) => v.value)
            .filter(Boolean)
            .join(', ') || undefined
      }));

      const totalValue = parseFloat(totals.total_price) / currencyDivider;
      const couponCode = coupons.length > 0 ? coupons[0].code : undefined;

      this.googleAnalyticsService.trackBeginCheckout({
        currency: totals.currency_code,
        value: totalValue,
        items: items,
        coupon: couponCode
      });
    } catch (error) {
      console.error('Failed to track begin checkout:', error);
    }
  }

  /**
   * Track add_shipping_info event in Google Analytics
   */
  private trackAddShippingInfo(shippingMethod: string): void {
    try {
      const cartItems = this.cartItems();
      const totals = this.totals();
      const coupons = this.coupons();

      if (cartItems.length === 0) {
        return;
      }

      const currencyDivider = 10 ** (totals.currency_minor_unit || 2);

      const items: GA4Item[] = cartItems.map((item, index) => ({
        item_id: item.id.toString(),
        item_name: convertHtml2Text(item.name),
        price: parseFloat(item.prices.price) / currencyDivider,
        quantity: item.quantity,
        index,
        item_variant:
          item.variation
            ?.map((v) => v.value)
            .filter(Boolean)
            .join(', ') || undefined
      }));

      const totalValue = parseFloat(totals.total_price) / currencyDivider;
      const couponCode = coupons.length > 0 ? coupons[0].code : undefined;

      this.googleAnalyticsService.trackAddShippingInfo({
        currency: totals.currency_code,
        value: totalValue,
        shipping_tier: shippingMethod,
        items: items,
        coupon: couponCode
      });
    } catch (error) {
      console.error('Failed to track add shipping info:', error);
    }
  }

  /**
   * Track add_payment_info event in Google Analytics
   */
  private trackAddPaymentInfo(paymentType: string): void {
    try {
      const cartItems = this.cartItems();
      const totals = this.totals();
      const coupons = this.coupons();

      if (cartItems.length === 0) {
        return;
      }

      const currencyDivider = 10 ** (totals.currency_minor_unit || 2);

      const items: GA4Item[] = cartItems.map((item, index) => ({
        item_id: item.id.toString(),
        item_name: convertHtml2Text(item.name),
        price: parseFloat(item.prices.price) / currencyDivider,
        quantity: item.quantity,
        index,
        item_variant:
          item.variation
            ?.map((v) => v.value)
            .filter(Boolean)
            .join(', ') || undefined
      }));

      const totalValue = parseFloat(totals.total_price) / currencyDivider;
      const couponCode = coupons.length > 0 ? coupons[0].code : undefined;

      this.googleAnalyticsService.trackAddPaymentInfo({
        currency: totals.currency_code,
        value: totalValue,
        payment_type: paymentType,
        items: items,
        coupon: couponCode
      });
    } catch (error) {
      console.error('Failed to track add payment info:', error);
    }
  }

  /**
   * Track purchase event in Google Analytics
   * This is the most important event for e-commerce tracking and revenue reporting
   */
  private trackPurchaseEvent(order: WooCommerceCheckoutResponse): void {
    try {
      const cartItems = this.cartItems();
      const totals = this.totals();
      const shippingStateSnapshot = this.store.selectSnapshot(
        (state: any) => state.shipping
      );
      const coupons = this.coupons();

      const currencyDivider = 10 ** (totals.currency_minor_unit || 2);

      // Map cart items to GA4 format with all required fields
      const items: GA4Item[] = cartItems.map((item, index) => ({
        item_id: item.id.toString(),
        item_name: convertHtml2Text(item.name),
        price: parseFloat(item.prices.price) / currencyDivider,
        quantity: item.quantity,
        index,
        item_variant:
          item.variation
            ?.map((v) => v.value)
            .filter(Boolean)
            .join(', ') || undefined
      }));

      // Calculate values - these are critical for revenue reporting
      const totalValue = parseFloat(totals.total_price) / currencyDivider;
      const taxValue = parseFloat(totals.total_tax || '0') / currencyDivider;
      const shippingValue =
        parseFloat(totals.total_shipping || '0') / currencyDivider;

      // Determine shipping method name
      const shippingMethod =
        shippingStateSnapshot?.method === ShippingMethod.Delivery
          ? shippingStateSnapshot?.selectedShippingMethod?.name || 'Delivery'
          : 'Pickup';

      // Get coupon code if any
      const couponCode = coupons.length > 0 ? coupons[0].code : undefined;

      // Track the purchase with all e-commerce data
      this.googleAnalyticsService.trackPurchase({
        transaction_id: order.order_id.toString(),
        currency: totals.currency_code,
        value: totalValue,
        items: items,
        tax: taxValue,
        shipping: shippingValue,
        coupon: couponCode
      });
    } catch (error) {
      console.error('Failed to track purchase event:', error);
    }
  }

  private hasAvailableSlots(
    response: WooCommercePickupLocationDatetimeResponse | any
  ): boolean {
    if (!response) {
      return false;
    }

    // Allow scheduling if delivery_dates exist (even with empty/only-asap time_slots)
    // Working hours from get_site_info will be used as fallback for time selection
    const hasDeliveryDates =
      response.delivery_dates &&
      Array.isArray(response.delivery_dates) &&
      response.delivery_dates.length > 0;

    if (hasDeliveryDates) {
      const timezone = this.store.selectSnapshot(RestaurantState.timezone);
      const enabledDates = filterDatesByWeekdayAvailability(
        response.delivery_dates.map((deliveryDate: { date: string }) =>
          convertToDate(deliveryDate.date)
        ),
        response.weekdays,
        timezone
      );

      return enabledDates.length > 0;
    }

    const hasTimeSlots =
      response.delivery_dates &&
      response.delivery_dates.some(
        (date: any) => date.time_slots && date.time_slots.length > 0
      );

    // Check if restaurant has working hours available as fallback
    const workingHours = this.store.selectSnapshot(
      RestaurantState.workingHours
    );
    const hasWorkingHours =
      workingHours && workingHours.some((h: any) => h.is_opened);

    return !!hasTimeSlots || !!hasWorkingHours;
  }
}
